<!-- TestCreationForm.vue - Улучшенная версия -->
<template>
	<div class="test-creation-form">
		<div class="test-creation-form__container">
			<div v-if="isOffline" class="form-offline-banner" role="status">
				Нет интернета. Сохранение будет поставлено в очередь ({{ pendingQueueCount }}).
			</div>
			<!-- Настройки теста -->
			<div class="test-settings">
				<div class="test-settings__header">
					<div class="test-settings__header-title">
						{{ isEditMode ? 'Редактирование теста' : 'Создание теста' }}
						<span
							v-if="!isFormValid && showErrors"
							class="test-settings__validation-badge test-settings__validation-badge--error"
							:title="validationMessage"
						>
							<svg
								width="20"
								height="20"
								viewBox="0 0 24 24"
								style="position: relative; top: 3px"
							>
								<circle
									cx="12"
									cy="12"
									r="9"
									fill="none"
									stroke="#fff"
									stroke-width="1"
								/>
								<path fill="#fff" d="M12 8h-1v5h2V8zm0 7h-1v2h2v-2z" />
							</svg>
						</span>
						<span
							v-else-if="isFormValid"
							class="test-settings__validation-badge test-settings__validation-badge--success"
							title="Все поля заполнены корректно"
						>
							<svg
								width="20"
								height="20"
								viewBox="0 0 24 24"
								fill="none"
								xmlns="http://www.w3.org/2000/svg"
								style="position: relative; top: 3px"
							>
								<path
									d="M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18ZM8 11.5l3 3 5-5"
									stroke="currentColor"
									stroke-width="2"
									stroke-linecap="round"
									stroke-linejoin="round"
								/>
							</svg>
						</span>
					</div>
					<button
						class="test-settings__close-button"
						type="button"
						@click="handleClose"
						aria-label="Закрыть"
					>
						<img
							class="test-settings__close-icon"
							alt="Закрыть"
							src="@/assets/cdn-animaapp/vector-8.svg"
						/>
					</button>
				</div>

				<!-- Табы -->
				<div class="test-tabs">
					<button
						v-for="tab in tabs"
						:key="tab.id"
						class="test-tabs__item"
						:class="{ 'test-tabs__item--active': activeTab === tab.id }"
						@click="activeTab = tab.id"
					>
						<span class="test-tabs__icon">{{ tab.icon }}</span>
						<span class="test-tabs__label">{{ tab.label }}</span>
						<span v-if="tab.badge" class="test-tabs__badge">{{
							tab.badge
						}}</span>
					</button>
				</div>

				<!-- Индикатор автосохранения -->
				<div v-if="lastSaveTimeFormatted" class="test-settings__autosave">
					<span class="test-settings__autosave-text">
						Последнее сохранение: {{ lastSaveTimeFormatted }}
					</span>
				</div>

				<!-- ===================== -->
				<!-- TAB: НАСТРОЙКИ -->
				<!-- ===================== -->
				<div v-show="activeTab === 'settings'" class="test-tab-content">

					<!-- Основная информация -->
					<div class="test-section">
						<h4 class="test-section__title">Основная информация</h4>
						<div class="test-section__card">
							<div class="test-field">
								<label class="test-field__label">
									Название теста <span class="test-field__required">*</span>
								</label>
								<input
									v-model="testName"
									type="text"
									class="test-field__input"
									:class="{ 'test-field__input--error': errors.testName }"
									placeholder="Введите название теста"
									maxlength="200"
								/>
								<div class="test-field__footer">
									<span v-if="errors.testName" class="test-field__error">
										{{ errors.testName }}
									</span>
									<span class="test-field__hint"
										>{{ testName.length }}/200</span
									>
								</div>
							</div>

							<div class="test-field">
								<label class="test-field__label">Описание теста</label>
								<textarea
									v-model="testDescription"
									class="test-field__textarea"
									placeholder="Краткое описание теста (опционально)"
									rows="3"
									maxlength="500"
								/>
								<div class="test-field__footer">
									<span class="test-field__hint"
										>{{ testDescription.length }}/500</span
									>
								</div>
							</div>
						</div>
					</div>

					<!-- Параметры прохождения -->
					<div class="test-section">
						<h4 class="test-section__title">Параметры прохождения</h4>
						<div class="test-section__card">
							<div class="test-field-row">
								<div class="test-field">
									<label class="test-field__label">Длительность (минут)</label>
									<input
										v-model.number="duration"
										type="number"
										min="1"
										max="300"
										class="test-field__input"
										placeholder="Без ограничения"
									/>
									<span class="test-field__hint">
										{{
											duration ? `${duration} мин` : 'Без ограничения времени'
										}}
									</span>
								</div>

								<div class="test-field">
									<label class="test-field__label">Проходной балл (%)</label>
									<div class="test-field__slider-wrapper">
										<input
											v-model.number="passingScore"
											type="number"
											min="0"
											max="100"
											class="test-field__input test-field__input--small"
										/>
										<input
											v-model.number="passingScore"
											type="range"
											min="0"
											max="100"
											class="test-field__slider"
										/>
									</div>
									<span class="test-field__hint"
										>{{ passingScore }}% для прохождения</span
									>
								</div>
							</div>

							<div class="test-field-row">
								<div class="test-field">
									<label class="test-field__label">Количество попыток</label>
									<input
										v-model.number="attempts"
										type="number"
										min="1"
										max="10"
										class="test-field__input"
									/>
									<span class="test-field__hint">
										{{ attempts === 1 ? '1 попытка' : `${attempts} попыток` }}
									</span>
								</div>

								<div class="test-field">
									<label class="test-field__label">Баллы за вопрос</label>
									<input
										v-model.number="pointsPerQuestion"
										type="number"
										min="1"
										max="100"
										class="test-field__input"
									/>
									<span class="test-field__hint"
										>Всего баллов: {{ totalPoints }}</span
									>
								</div>
							</div>
							<!-- После существующих полей в секции "Параметры прохождения" -->

							<!-- Выбор случайных вопросов из пула -->
							<div class="test-section">
								<h4 class="test-section__title">Выбор вопросов для теста</h4>
								<div class="test-section__card">
									<!-- Основной чекбокс -->
									<label class="test-checkbox">
										<input
											v-model="selectRandomQuestions"
											type="checkbox"
											class="test-checkbox__input"
											:disabled="questions.length === 0"
										/>
										<span class="test-checkbox__custom"></span>
										<span class="test-checkbox__text">
											Выбирать случайные вопросы из пула
											<span
												v-if="questions.length > 0"
												class="test-checkbox__hint"
											>
												(доступно {{ questions.length }} вопросов)
											</span>
										</span>
									</label>

									<!-- Настройки случайного выбора -->
									<div
										v-if="selectRandomQuestions"
										class="test-random-selection"
									>
										<!-- Количество вопросов -->
										<div class="test-field">
											<label class="test-field__label">
												Количество вопросов в тесте
											</label>
											<div class="test-field__row">
												<input
													v-model.number="questionsToShow"
													type="number"
													:min="1"
													:max="questions.length"
													class="test-field__input test-field__input--small"
													:placeholder="questions.length"
												/>
												<span class="test-field__suffix">
													из {{ questions.length }}
												</span>
											</div>
											<span class="test-field__hint">
												{{
													questionsToShow
														? `Каждый студент получит ${questionsToShow} случайных вопросов`
														: 'Все вопросы будут в тесте'
												}}
											</span>
										</div>

										<!-- Статистика по типам -->
										<div class="test-question-pool-stats">
											<div class="pool-stat">
												<span class="pool-stat__icon">◉</span>
												<span class="pool-stat__label">Один ответ:</span>
												<span class="pool-stat__value">{{
													questionCountByType.single
												}}</span>
											</div>
											<div class="pool-stat">
												<span class="pool-stat__icon">☑</span>
												<span class="pool-stat__label">Несколько ответов:</span>
												<span class="pool-stat__value">{{
													questionCountByType.multiple
												}}</span>
											</div>
											<div class="pool-stat">
												<span class="pool-stat__icon">✎</span>
												<span class="pool-stat__label">Текстовый ответ:</span>
												<span class="pool-stat__value">{{
													questionCountByType.text
												}}</span>
											</div>
										</div>

										<!-- Режим распределения -->
										<div class="test-field">
											<label class="test-field__label">
												Распределение по типам вопросов
											</label>
											<div class="test-distribution-modes">
												<label
													class="test-distribution-mode"
													:class="{
														'test-distribution-mode--active':
															questionDistributionMode === 'random',
													}"
												>
													<input
														v-model="questionDistributionMode"
														type="radio"
														value="random"
														class="test-distribution-mode__radio"
													/>
													<div class="test-distribution-mode__content">
														<span class="test-distribution-mode__icon">🎲</span>
														<span class="test-distribution-mode__title"
															>Случайное</span
														>
														<span class="test-distribution-mode__desc"
															>Любые вопросы из пула</span
														>
													</div>
												</label>

												<label
													class="test-distribution-mode"
													:class="{
														'test-distribution-mode--active':
															questionDistributionMode === 'proportional',
													}"
												>
													<input
														v-model="questionDistributionMode"
														type="radio"
														value="proportional"
														class="test-distribution-mode__radio"
													/>
													<div class="test-distribution-mode__content">
														<span class="test-distribution-mode__icon">📊</span>
														<span class="test-distribution-mode__title"
															>По процентам</span
														>
														<span class="test-distribution-mode__desc"
															>Пропорция типов</span
														>
													</div>
												</label>

												<label
													class="test-distribution-mode"
													:class="{
														'test-distribution-mode--active':
															questionDistributionMode === 'fixed',
													}"
												>
													<input
														v-model="questionDistributionMode"
														type="radio"
														value="fixed"
														class="test-distribution-mode__radio"
													/>
													<div class="test-distribution-mode__content">
														<span class="test-distribution-mode__icon">🔢</span>
														<span class="test-distribution-mode__title"
															>Фиксированное</span
														>
														<span class="test-distribution-mode__desc"
															>Точное количество</span
														>
													</div>
												</label>
											</div>
										</div>

										<!-- Настройки процентного распределения -->
										<div
											v-if="questionDistributionMode === 'proportional'"
											class="test-distribution-settings"
										>
											<div class="test-distribution-row">
												<div class="test-distribution-item">
													<label class="test-distribution-item__label">
														<span class="test-distribution-item__icon">◉</span>
														Один ответ
														<span class="test-distribution-item__available">
															(доступно: {{ questionCountByType.single }})
														</span>
													</label>
													<div class="test-distribution-item__input">
														<input
															v-model.number="questionTypeDistribution.single"
															type="number"
															min="0"
															max="100"
															class="test-field__input test-field__input--small"
															placeholder="0"
														/>
														<span class="test-distribution-item__suffix"
															>%</span
														>
													</div>
												</div>

												<div class="test-distribution-item">
													<label class="test-distribution-item__label">
														<span class="test-distribution-item__icon">☑</span>
														Несколько ответов
														<span class="test-distribution-item__available">
															(доступно: {{ questionCountByType.multiple }})
														</span>
													</label>
													<div class="test-distribution-item__input">
														<input
															v-model.number="questionTypeDistribution.multiple"
															type="number"
															min="0"
															max="100"
															class="test-field__input test-field__input--small"
															placeholder="0"
														/>
														<span class="test-distribution-item__suffix"
															>%</span
														>
													</div>
												</div>

												<div class="test-distribution-item">
													<label class="test-distribution-item__label">
														<span class="test-distribution-item__icon">✎</span>
														Текстовый ответ
														<span class="test-distribution-item__available">
															(доступно: {{ questionCountByType.text }})
														</span>
													</label>
													<div class="test-distribution-item__input">
														<input
															v-model.number="questionTypeDistribution.text"
															type="number"
															min="0"
															max="100"
															class="test-field__input test-field__input--small"
															placeholder="0"
														/>
														<span class="test-distribution-item__suffix"
															>%</span
														>
													</div>
												</div>
											</div>

											<!-- Индикатор суммы процентов -->
											<div
												class="test-distribution-total"
												:class="{
													'test-distribution-total--valid':
														distributionPercentageSum === 100,
													'test-distribution-total--error':
														distributionPercentageSum !== 100 &&
														distributionPercentageSum !== 0,
												}"
											>
												<span>Сумма: {{ distributionPercentageSum }}%</span>
												<span
													v-if="
														distributionPercentageSum !== 100 &&
														distributionPercentageSum !== 0
													"
												>
													(должно быть 100%)
												</span>
											</div>
										</div>

										<!-- Настройки фиксированного количества -->
										<div
											v-if="questionDistributionMode === 'fixed'"
											class="test-distribution-settings"
										>
											<div class="test-distribution-row">
												<div class="test-distribution-item">
													<label class="test-distribution-item__label">
														<span class="test-distribution-item__icon">◉</span>
														Один ответ
														<span class="test-distribution-item__available">
															(макс: {{ questionCountByType.single }})
														</span>
													</label>
													<div class="test-distribution-item__input">
														<input
															v-model.number="questionTypeDistribution.single"
															type="number"
															min="0"
															:max="questionCountByType.single"
															class="test-field__input test-field__input--small"
															placeholder="0"
														/>
														<span class="test-distribution-item__suffix"
															>шт</span
														>
													</div>
												</div>

												<div class="test-distribution-item">
													<label class="test-distribution-item__label">
														<span class="test-distribution-item__icon">☑</span>
														Несколько ответов
														<span class="test-distribution-item__available">
															(макс: {{ questionCountByType.multiple }})
														</span>
													</label>
													<div class="test-distribution-item__input">
														<input
															v-model.number="questionTypeDistribution.multiple"
															type="number"
															min="0"
															:max="questionCountByType.multiple"
															class="test-field__input test-field__input--small"
															placeholder="0"
														/>
														<span class="test-distribution-item__suffix"
															>шт</span
														>
													</div>
												</div>

												<div class="test-distribution-item">
													<label class="test-distribution-item__label">
														<span class="test-distribution-item__icon">✎</span>
														Текстовый ответ
														<span class="test-distribution-item__available">
															(макс: {{ questionCountByType.text }})
														</span>
													</label>
													<div class="test-distribution-item__input">
														<input
															v-model.number="questionTypeDistribution.text"
															type="number"
															min="0"
															:max="questionCountByType.text"
															class="test-field__input test-field__input--small"
															placeholder="0"
														/>
														<span class="test-distribution-item__suffix"
															>шт</span
														>
													</div>
												</div>
											</div>

											<!-- Индикатор суммы -->
											<div
												class="test-distribution-total"
												:class="{
													'test-distribution-total--valid':
														distributionFixedSum === effectiveQuestionsCount,
													'test-distribution-total--warning':
														distributionFixedSum < effectiveQuestionsCount &&
														distributionFixedSum > 0,
													'test-distribution-total--error':
														distributionFixedSum > effectiveQuestionsCount,
												}"
											>
												<span
													>Указано: {{ distributionFixedSum }} из
													{{ effectiveQuestionsCount }}</span
												>
												<span
													v-if="
														distributionFixedSum < effectiveQuestionsCount &&
														distributionFixedSum > 0
													"
												>
													({{ effectiveQuestionsCount - distributionFixedSum }}
													добавятся случайно)
												</span>
											</div>
										</div>

										<!-- Сообщение о валидации -->
										<div
											v-if="distributionValidation.message"
											class="test-distribution-message"
											:class="{
												'test-distribution-message--error':
													!distributionValidation.valid,
												'test-distribution-message--success':
													distributionValidation.valid,
											}"
										>
											{{ distributionValidation.message }}
										</div>
									</div>
								</div>
							</div>
						</div>
					</div>

					<!-- В разделе "Дополнительные настройки" -->
					<div class="test-section">
						<h4 class="test-section__title">Дополнительные настройки</h4>
						<div class="test-section__card">
							<div class="test-checkbox-group">
								<!-- Существующие настройки -->
								<label class="test-checkbox">
									<input
										v-model="randomOrder"
										type="checkbox"
										class="test-checkbox__input"
									/>
									<span class="test-checkbox__custom"></span>
									<span class="test-checkbox__text">
										Случайный порядок вопросов
									</span>
								</label>

								<label class="test-checkbox">
									<input
										v-model="showCorrectAnswers"
										type="checkbox"
										class="test-checkbox__input"
									/>
									<span class="test-checkbox__custom"></span>
									<span class="test-checkbox__text">
										Показывать правильные ответы после прохождения
									</span>
								</label>

								<label class="test-checkbox">
									<input
										v-model="allowReview"
										type="checkbox"
										class="test-checkbox__input"
									/>
									<span class="test-checkbox__custom"></span>
									<span class="test-checkbox__text">
										Разрешить возвращаться к вопросам
									</span>
								</label>

								<label class="test-checkbox">
									<input
										v-model="penalizeWrong"
										type="checkbox"
										class="test-checkbox__input"
									/>
									<span class="test-checkbox__custom"></span>
									<span class="test-checkbox__text">
										Вычитать баллы за неправильные ответы
									</span>
								</label>

								<!-- НОВЫЕ НАСТРОЙКИ ДОБАВЬ ЗДЕСЬ -->
								<label class="test-checkbox">
									<input
										v-model="disableCopying"
										type="checkbox"
										class="test-checkbox__input"
									/>
									<span class="test-checkbox__custom"></span>
									<span class="test-checkbox__text">
										Запретить копирование текста
									</span>
								</label>

								<label class="test-checkbox">
									<input
										v-model="limitTimePerQuestion"
										type="checkbox"
										class="test-checkbox__input"
									/>
									<span class="test-checkbox__custom"></span>
									<span class="test-checkbox__text">
										Лимит времени на каждый вопрос
									</span>
								</label>

								<div v-if="penalizeWrong" class="test-checkbox__indent-field">
									<label class="test-field__label">Процент штрафа</label>
									<input
										v-model.number="penaltyPercentage"
										type="number"
										min="0"
										max="100"
										class="test-field__input test-field__input--small"
									/>
								</div>

								<!-- Поле для времени на вопрос -->
								<div
									v-if="limitTimePerQuestion"
									class="test-checkbox__indent-field"
								>
									<label class="test-field__label"
										>Время на вопрос (секунд)</label
									>
									<input
										v-model.number="timePerQuestion"
										type="number"
										min="10"
										max="300"
										class="test-field__input test-field__input--small"
										placeholder="60"
									/>
								</div>
							</div>
						</div>
					</div>

					<!-- НОВАЯ СЕКЦИЯ: Импорт/Экспорт -->
					<div class="test-section">
						<h4 class="test-section__title">Импорт и экспорт</h4>
						<div class="test-section__card">
							<div class="test-import-export">
								<!-- Импорт -->
								<div class="test-import-export__item">
									<div class="test-import-export__info">
										<div class="test-import-export__icon">
											<svg
												fill="#000000"
												width="45px"
												height="45px"
												viewBox="0 0 24 24"
												xmlns="http://www.w3.org/2000/svg"
											>
												<path
													d="M21,14a1,1,0,0,0-1,1v4a1,1,0,0,1-1,1H5a1,1,0,0,1-1-1V15a1,1,0,0,0-2,0v4a3,3,0,0,0,3,3H19a3,3,0,0,0,3-3V15A1,1,0,0,0,21,14Zm-9.71,1.71a1,1,0,0,0,.33.21.94.94,0,0,0,.76,0,1,1,0,0,0,.33-.21l4-4a1,1,0,0,0-1.42-1.42L13,12.59V3a1,1,0,0,0-2,0v9.59l-2.29-2.3a1,1,0,1,0-1.42,1.42Z"
												/>
											</svg>
										</div>
										<div class="test-import-export__text">
											<div class="test-import-export__title">
												Импорт вопросов
											</div>
											<div class="test-import-export__description">
												Загрузите вопросы из JSON или DOCX файла
											</div>
										</div>
									</div>
									<div class="test-import-export__buttons">
										<button
											type="button"
											class="test-btn test-btn--secondary"
											@click="handleImportQuestionsJson"
											title="Импорт из JSON"
										>
											<span>JSON</span>
										</button>
										<button
											type="button"
											class="test-btn test-btn--secondary"
											@click="openDocxImportModal"
											title="Импорт из DOCX"
											style="margin-left: 8px"
										>
											<span>DOCX</span>
										</button>
									</div>
								</div>

								<div class="test-import-export__divider"></div>

								<!-- Экспорт -->
								<div class="test-import-export__item">
									<div class="test-import-export__info">
										<div class="test-import-export__icon">
											<svg
												width="45"
												height="45"
												viewBox="0 0 24 24"
												fill="none"
												xmlns="http://www.w3.org/2000/svg"
												style="position: relative; left: 3px; top: 3px"
											>
												<path
													d="M10.5684 0.250174C10.4227 0.0907898 10.2167 0 10.0007 0C9.78471 0 9.57876 0.0907898 9.43301 0.250174L5.33043 4.73735C5.04376 5.05089 5.06555 5.53746 5.37909 5.82412C5.69263 6.11079 6.17919 6.089 6.46586 5.77547L9.23148 2.75056V14.1026C9.23148 14.5274 9.57589 14.8718 10.0007 14.8718C10.4255 14.8718 10.7699 14.5274 10.7699 14.1026V2.75056L13.5356 5.77547C13.8222 6.089 14.3088 6.11079 14.6223 5.82412C14.9359 5.53746 14.9576 5.05089 14.671 4.73735L10.5684 0.250174Z"
													stroke-width="3"
													fill="black"
												/>
												<path
													d="M1.53846 13.0768C1.53846 12.652 1.19407 12.3076 0.769231 12.3076C0.344401 12.3076 5.37388e-07 12.652 5.37388e-07 13.0768V13.1332C-1.99754e-05 14.5358 -4.05617e-05 15.6664 0.119508 16.5556C0.243631 17.4788 0.50916 18.256 1.12651 18.8734C1.74387 19.4908 2.52117 19.7563 3.44436 19.8804C4.33356 19.9999 5.46414 19.9999 6.8668 19.9999H13.1332C14.5359 19.9999 15.6665 19.9999 16.5557 19.8804C17.4789 19.7563 18.2561 19.4908 18.8735 18.8734C19.4909 18.256 19.7564 17.4788 19.8805 16.5556C20 15.6664 20 14.5358 20 13.1332V13.0768C20 12.652 19.6556 12.3076 19.2308 12.3076C18.806 12.3076 18.4615 12.652 18.4615 13.0768C18.4615 14.5491 18.4599 15.5758 18.3558 16.3506C18.2546 17.1032 18.0695 17.5017 17.7856 17.7856C17.5017 18.0695 17.1033 18.2545 16.3507 18.3557C15.5759 18.4598 14.5491 18.4615 13.0769 18.4615H6.92308C5.45086 18.4615 4.42407 18.4598 3.64935 18.3557C2.89677 18.2545 2.49823 18.0695 2.21437 17.7856C1.93051 17.5017 1.74544 17.1032 1.64426 16.3506C1.54009 15.5758 1.53846 14.5491 1.53846 13.0768Z"
													stroke-width="3"
													fill="black"
												/>
											</svg>
										</div>
										<div class="test-import-export__text">
											<div class="test-import-export__title">Экспорт теста</div>
											<div class="test-import-export__description">
												Сохраните тест в файл для резервного копирования
											</div>
										</div>
									</div>
									<div class="test-import-export__buttons">
										<button
											type="button"
											class="test-btn test-btn--secondary"
											@click="handleExport('json')"
											:disabled="questions.length === 0"
											title="Экспорт в JSON"
										>
											<span>JSON</span>
										</button>
										<button
											type="button"
											class="test-btn test-btn--secondary"
											@click="handleExport('docx')"
											:disabled="questions.length === 0"
											title="Экспорт в DOCX"
											style="margin-left: 8px"
										>
											<span>DOCX</span>
										</button>
									</div>
								</div>
							</div>

							<!-- Статистика (если есть вопросы) -->
							<div
								v-if="questions.length > 0"
								class="test-import-export__stats"
								style="
									margin-top: 30px;
									display: flex;
									flex-direction: column;
									gap: 8px;
								"
							>
								<div class="test-import-export__stat">
									<span class="test-import-export__stat-label"
										>Всего вопросов:
									</span>
									<span class="test-import-export__stat-value">{{
										questions.length
									}}</span>
								</div>
								<div class="test-import-export__stat">
									<span class="test-import-export__stat-label"
										>Общий балл:
									</span>
									<span class="test-import-export__stat-value">{{
										totalPoints
									}}</span>
								</div>
							</div>

							<!-- Подсказка о форматах -->
							<div class="test-import-export__hint">
								<span class="test-import-export__hint-icon">💡</span>
								<span class="test-import-export__hint-text">
									Поддерживаемые форматы: JSON, DOCX. Максимальный размер файла:
									5 МБ
								</span>
							</div>
						</div>
					</div>
				</div>

				<!-- ===================== -->
				<!-- TAB: ВОПРОСЫ -->
				<!-- ===================== -->
				<div v-show="activeTab === 'questions'" class="test-tab-content">
					<!-- Тулбар -->
					<div class="test-questions-toolbar">
						<div class="test-questions-toolbar__left">
							<button
								type="button"
								class="test-btn test-btn--primary"
								@click="handleAddQuestion"
							>
								<span class="test-btn__icon">+</span>
								<span>Добавить вопрос</span>
							</button>
						</div>

						<div class="test-questions-toolbar__right">
							<div class="test-search">
								<span class="test-search__icon">🔍</span>
								<input
									v-model="questionSearch"
									type="text"
									class="test-search__input"
									placeholder="Поиск вопросов..."
								/>
							</div>

							<button
								v-if="questions.length > 1"
								type="button"
								class="test-btn test-btn--ghost"
								@click="toggleAllQuestions"
							>
								{{ allExpanded ? 'Свернуть все' : 'Развернуть все' }}
							</button>
						</div>
					</div>

					<!-- Пустое состояние -->
					<div
						v-if="filteredQuestions.length === 0 && !questionSearch"
						class="test-empty-state"
					>
						<div class="test-empty-state__icon">❓</div>
						<h3 class="test-empty-state__title">Вопросы не добавлены</h3>
						<p class="test-empty-state__text">
							Начните создавать тест, добавив первый вопрос
						</p>
						<button
							type="button"
							class="test-btn test-btn--primary test-btn--large"
							@click="handleAddQuestion"
						>
							<span class="test-btn__icon">+</span>
							Добавить первый вопрос
						</button>
					</div>

					<!-- Нет результатов поиска -->
					<div
						v-else-if="filteredQuestions.length === 0 && questionSearch"
						class="test-empty-state"
					>
						<div class="test-empty-state__icon">🔍</div>
						<h3 class="test-empty-state__title">Ничего не найдено</h3>
						<p class="test-empty-state__text">
							Попробуйте изменить поисковый запрос
						</p>
					</div>

					<!-- Список вопросов с Drag & Drop -->
					<div v-else class="test-questions-list">
						<div
							v-for="(question, index) in filteredQuestions"
							:key="question.id"
							class="test-question"
							:class="{
								'test-question--expanded': question.isExpanded,
								'test-question--error': hasQuestionErrors(question),
								'test-question--dragging': draggedQuestionIndex === index,
								'test-question--drag-over': dragOverQuestionIndex === index,
							}"
							draggable="true"
							@dragstart="handleQuestionDragStart($event, index)"
							@dragover="handleQuestionDragOver($event, index)"
							@dragleave="handleQuestionDragLeave(index)"
							@drop="handleQuestionDrop($event, index)"
							@dragend="handleQuestionDragEnd"
						>
							<div
								class="test-question__header"
								@click="toggleQuestion(question.id)"
							>
								<div class="test-question__left">
									<!-- Ручка для перетаскивания -->
									<div
										class="test-question__drag"
										title="Перетащите для изменения порядка"
										@mousedown.stop
									>
										⋮⋮
									</div>

									<div class="test-question__number">
										Вопрос {{ index + 1 }}
										<span
											v-if="hasQuestionErrors(question)"
											class="test-question__error-badge"
											:title="getQuestionErrorsText(question)"
										>
											⚠️
										</span>
									</div>

									<div v-if="question.text" class="test-question__preview">
										{{ truncate(question.text, 50) }}
									</div>
									<div
										v-else
										class="test-question__preview test-question__preview--empty"
									>
										Без текста
									</div>
								</div>

								<div class="test-question__right">
									<span class="test-question__type-badge">
										{{ getQuestionTypeLabel(question.type) }}
									</span>

									<!-- Кнопки перемещения -->
									<button
										v-if="index > 0"
										type="button"
										class="test-question__action"
										@click.stop="moveQuestionUp(index)"
										title="Переместить вверх"
									>
										↑
									</button>

									<button
										v-if="index < questions.length - 1"
										type="button"
										class="test-question__action"
										@click.stop="moveQuestionDown(index)"
										title="Переместить вниз"
									>
										↓
									</button>

									<button
										type="button"
										class="test-question__action"
										@click.stop="duplicateQuestion(index)"
										title="Дублировать"
									>
										<svg
											width="22"
											height="25"
											viewBox="0 0 22 25"
											fill="none"
											xmlns="http://www.w3.org/2000/svg"
										>
											<path
												d="M15.625 21.875V23.8281C15.625 24.4753 15.1003 25 14.4531 25H1.17188C0.524658 25 0 24.4753 0 23.8281V5.85938C0 5.21216 0.524658 4.6875 1.17188 4.6875H4.6875V19.1406C4.6875 20.6484 5.91411 21.875 7.42188 21.875H15.625ZM15.625 5.07812V0H7.42188C6.77466 0 6.25 0.524658 6.25 1.17188V19.1406C6.25 19.7878 6.77466 20.3125 7.42188 20.3125H20.7031C21.3503 20.3125 21.875 19.7878 21.875 19.1406V6.25H16.7969C16.1523 6.25 15.625 5.72266 15.625 5.07812ZM21.5318 3.56304L18.312 0.343213C18.0922 0.123458 17.7941 1.62498e-06 17.4833 0L17.1875 0V4.6875H21.875V4.39165C21.875 4.08086 21.7515 3.7828 21.5318 3.56304Z"
												fill="black"
												fill-opacity="0.8"
											/>
										</svg>
									</button>

									<button
										type="button"
										class="test-question__action"
										@click.stop="toggleQuestion(question.id)"
										:title="question.isExpanded ? 'Свернуть' : 'Развернуть'"
									>
										<svg
											v-if="question.isExpanded"
											width="20"
											height="13"
											viewBox="0 0 20 13"
											fill="none"
											xmlns="http://www.w3.org/2000/svg"
										>
											<path
												d="M10.4883 0.527344L18.457 8.49609C19.043 9.08203 19.043 9.96094 18.457 10.4883L17.168 11.8359C16.582 12.3633 15.7031 12.3633 15.1758 11.8359L9.55078 6.15234L3.86719 11.8359C3.33984 12.3633 2.46094 12.3633 1.875 11.8359L0.585938 10.4883C0 9.96094 0 9.08203 0.585938 8.49609L8.55469 0.527344C9.08203 0 9.96094 0 10.4883 0.527344Z"
												fill="black"
												fill-opacity="0.7"
											/>
										</svg>
										<svg
											v-else
											width="20"
											height="13"
											viewBox="0 0 20 13"
											fill="none"
											xmlns="http://www.w3.org/2000/svg"
										>
											<path
												d="M8.55469 11.8359L0.585938 3.86719C0 3.28125 0 2.40234 0.585938 1.875L1.875 0.527344C2.46094 0 3.33984 0 3.86719 0.527344L9.49219 6.21094L15.1758 0.527344C15.7031 0 16.582 0 17.168 0.527344L18.457 1.875C19.043 2.40234 19.043 3.28125 18.457 3.86719L10.4883 11.8359C9.96094 12.3633 9.08203 12.3633 8.55469 11.8359Z"
												fill="black"
												fill-opacity="0.7"
											/>
										</svg>
									</button>

									<button
										type="button"
										class="test-question__action test-question__action--danger"
										@click.stop="handleRemoveQuestion(index)"
										title="Удалить"
									>
										<svg
											width="22"
											height="25"
											viewBox="0 0 22 25"
											fill="none"
											xmlns="http://www.w3.org/2000/svg"
										>
											<path
												d="M1.5625 22.6563C1.5625 23.2779 1.80943 23.874 2.24897 24.3135C2.68851 24.7531 3.28465 25 3.90625 25H17.9688C18.5904 25 19.1865 24.7531 19.626 24.3135C20.0656 23.874 20.3125 23.2779 20.3125 22.6563V6.25001H1.5625V22.6563ZM14.8438 10.1563C14.8438 9.94906 14.9261 9.75034 15.0726 9.60383C15.2191 9.45732 15.4178 9.37501 15.625 9.37501C15.8322 9.37501 16.0309 9.45732 16.1774 9.60383C16.3239 9.75034 16.4062 9.94906 16.4062 10.1563V21.0938C16.4062 21.301 16.3239 21.4997 16.1774 21.6462C16.0309 21.7927 15.8322 21.875 15.625 21.875C15.4178 21.875 15.2191 21.7927 15.0726 21.6462C14.9261 21.4997 14.8438 21.301 14.8438 21.0938V10.1563ZM10.1562 10.1563C10.1562 9.94906 10.2386 9.75034 10.3851 9.60383C10.5316 9.45732 10.7303 9.37501 10.9375 9.37501C11.1447 9.37501 11.3434 9.45732 11.4899 9.60383C11.6364 9.75034 11.7188 9.94906 11.7188 10.1563V21.0938C11.7188 21.301 11.6364 21.4997 11.4899 21.6462C11.3434 21.7927 11.1447 21.875 10.9375 21.875C10.7303 21.875 10.5316 21.7927 10.3851 21.6462C10.2386 21.4997 10.1562 21.301 10.1562 21.0938V10.1563ZM5.46875 10.1563C5.46875 9.94906 5.55106 9.75034 5.69757 9.60383C5.84409 9.45732 6.0428 9.37501 6.25 9.37501C6.4572 9.37501 6.65591 9.45732 6.80243 9.60383C6.94894 9.75034 7.03125 9.94906 7.03125 10.1563V21.0938C7.03125 21.301 6.94894 21.4997 6.80243 21.6462C6.65591 21.7927 6.4572 21.875 6.25 21.875C6.0428 21.875 5.84409 21.7927 5.69757 21.6462C5.55106 21.4997 5.46875 21.301 5.46875 21.0938V10.1563ZM21.0938 1.56251H15.2344L14.7754 0.649423C14.6782 0.454215 14.5284 0.29001 14.3429 0.175281C14.1575 0.0605526 13.9437 -0.00014785 13.7256 8.5609e-06H8.14453C7.92694 -0.000827891 7.71352 0.0596463 7.52871 0.174503C7.34391 0.289359 7.19519 0.453951 7.09961 0.649423L6.64062 1.56251H0.78125C0.57405 1.56251 0.375336 1.64482 0.228823 1.79133C0.08231 1.93784 0 2.13656 0 2.34376L0 3.90626C0 4.11346 0.08231 4.31217 0.228823 4.45869C0.375336 4.6052 0.57405 4.68751 0.78125 4.68751H21.0938C21.301 4.68751 21.4997 4.6052 21.6462 4.45869C21.7927 4.31217 21.875 4.11346 21.875 3.90626V2.34376C21.875 2.13656 21.7927 1.93784 21.6462 1.79133C21.4997 1.64482 21.301 1.56251 21.0938 1.56251Z"
												fill="#FF6666"
											/>
										</svg>
									</button>
								</div>
							</div>

							<transition name="test-expand">
								<div
									v-show="question.isExpanded"
									class="test-question__content"
								>
									<!-- Текст вопроса -->
									<div class="test-field">
										<label class="test-field__label">
											Текст вопроса <span class="test-field__required">*</span>
										</label>
										<textarea
											v-model="question.text"
											class="test-field__textarea"
											:class="{
												'test-field__input--error':
													!question.text && showErrors,
											}"
											placeholder="Введите текст вопроса"
											rows="3"
											maxlength="1000"
										/>
										<div class="test-field__footer">
											<span class="test-field__hint"
												>{{ (question?.text || '').length }}/1000</span
											>
										</div>
									</div>

									<!-- Изображение к вопросу -->
									<div class="test-field">
										<label class="test-field__label"
											>Изображение (опционально)</label
										>
										<div class="test-image-upload">
											<div
												v-if="question.image"
												class="test-image-upload__preview"
											>
												<img
													:src="question.image"
													alt="Preview"
													class="test-image-upload__img"
												/>
												<button
													type="button"
													class="test-image-upload__remove"
													@click="removeQuestionImage(question)"
												>
													×
												</button>
											</div>
											<button
												v-else
												type="button"
												class="test-image-upload__button"
												@click="uploadQuestionImage(question)"
											>
												<span class="test-image-upload__icon">🖼️</span>
												<span>Загрузить изображение</span>
											</button>
											<span class="test-field__hint"
												>Максимальный размер: 5 МБ</span
											>
										</div>
									</div>

									<!-- Тип вопроса -->
									<div class="test-field">
										<label class="test-field__label">
											Тип вопроса <span class="test-field__required">*</span>
										</label>
										<div class="test-question-types">
											<label
												v-for="type in questionTypes"
												:key="type.value"
												class="test-question-type"
												:class="{
													'test-question-type--active':
														question.type === type.value,
												}"
											>
												<input
													v-model="question.type"
													type="radio"
													:value="type.value"
													class="test-question-type__radio"
													@change="handleQuestionTypeChange(question)"
												/>
												<div class="test-question-type__icon">
													{{ type.icon }}
												</div>
												<div class="test-question-type__label">
													{{ type.label }}
												</div>
												<div class="test-question-type__description">
													{{ type.description }}
												</div>
											</label>
										</div>
									</div>

									<!-- Варианты ответов (для single и multiple) -->
									<div
										v-if="
											question.type === 'single' || question.type === 'multiple'
										"
										class="test-field"
									>
										<div class="test-answers-header">
											<label class="test-field__label">
												Варианты ответов
												<span class="test-field__required">*</span>
											</label>
											<button
												type="button"
												class="test-btn test-btn--sm test-btn--secondary"
												@click="addAnswer(question)"
											>
												<span class="test-btn__icon">+</span>
												Добавить вариант
											</button>
										</div>

										<div class="test-answers-list">
											<div
												v-for="(answer, answerIndex) in question.answers"
												:key="answer.id"
												class="test-answer-wrapper"
											>
												<div
													class="test-answer"
													:class="{ 'test-answer--correct': answer.isCorrect }"
												>
													<button
														type="button"
														class="test-answer__drag"
														@mousedown="startAnswerDrag(question, answerIndex)"
													>
														⋮⋮
													</button>

													<input
														v-if="question.type === 'single'"
														type="radio"
														:name="`question-${question.id}`"
														:checked="answer.isCorrect"
														@change="setCorrectAnswer(question, answerIndex)"
														class="test-answer__radio"
													/>
													<input
														v-else
														v-model="answer.isCorrect"
														type="checkbox"
														class="test-answer__checkbox"
													/>

													<input
														v-model="answer.text"
														type="text"
														class="test-answer__input"
														:class="{
															'test-field__input--error':
																!answer.text && showErrors,
														}"
														:placeholder="`Вариант ${answerIndex + 1}`"
														maxlength="500"
													/>

													<button
														type="button"
														class="test-answer__image-btn"
														@click="uploadAnswerImage(answer)"
														:title="
															answer.image
																? 'Изменить изображение'
																: 'Добавить изображение'
														"
													>
														{{ answer.image ? '✓' : '🖼️' }}
													</button>

													<button
														type="button"
														class="test-answer__remove"
														@click="removeAnswer(question, answerIndex)"
														:disabled="
															!question.answers || question.answers.length <= 2
														"
													>
														×
													</button>
												</div>

												<div
													v-if="answer.image"
													class="test-answer__image-preview"
												>
													<img
														:src="answer.image"
														alt="Answer"
														class="test-answer__img"
													/>
													<button
														type="button"
														class="test-answer__image-remove"
														@click="removeAnswerImage(answer)"
													>
														×
													</button>
												</div>
											</div>
										</div>

										<div
											v-if="!hasCorrectAnswer(question) && showErrors"
											class="test-field__error"
										>
											Выберите хотя бы один правильный ответ
										</div>
									</div>

									<!-- Правильный ответ (для text) -->
									<div v-if="question.type === 'text'" class="test-field">
										<label class="test-field__label">
											Правильный ответ
											<span class="test-field__required">*</span>
										</label>
										<input
											v-model="question.correctText"
											type="text"
											class="test-field__input"
											:class="{
												'test-field__input--error':
													!question.correctText && showErrors,
											}"
											placeholder="Введите правильный ответ"
											maxlength="500"
										/>
										<label class="test-checkbox test-checkbox--sm">
											<input
												v-model="question.caseSensitive"
												type="checkbox"
												class="test-checkbox__input"
											/>
											<span class="test-checkbox__custom"></span>
											<span class="test-checkbox__text"
												>Учитывать регистр при проверке</span
											>
										</label>
									</div>

									<!-- Объяснение -->
									<div class="test-field">
										<label class="test-field__label"
											>Объяснение (опционально)</label
										>
										<textarea
											v-model="question.explanation"
											class="test-field__textarea"
											placeholder="Объяснение правильного ответа"
											rows="2"
											maxlength="500"
										/>
										<div class="test-field__footer">
											<span class="test-field__hint"
												>{{ (question?.explanation || '').length }}/500</span
											>
										</div>
									</div>

									<!-- Дополнительные настройки вопроса -->
									<details class="test-question-advanced">
										<summary class="test-question-advanced__toggle">
											Дополнительные настройки
										</summary>
										<div class="test-question-advanced__content">
											<div class="test-field-row">
												<div class="test-field">
													<label class="test-field__label"
														>Баллы за вопрос</label
													>
													<input
														v-model.number="question.customPoints"
														type="number"
														min="1"
														max="100"
														class="test-field__input test-field__input--small"
														:placeholder="`По умолчанию: ${pointsPerQuestion}`"
														style="
															width: 80px;
															height: 32px;
															font-size: 14px;
															padding: 0 8px;
														"
													/>
												</div>
											</div>
										</div>
									</details>
								</div>
							</transition>
						</div>
					</div>

					<!-- Статистика -->
					<div v-if="questions.length > 0" class="test-questions-stats">
						<div class="test-stat-card">
							<div class="test-stat-card__value">{{ questions.length }}</div>
							<div class="test-stat-card__label">Вопросов</div>
						</div>
						<div class="test-stat-card">
							<div class="test-stat-card__value">{{ totalPoints }}</div>
							<div class="test-stat-card__label">Всего баллов</div>
						</div>
						<div class="test-stat-card">
							<div class="test-stat-card__value">{{ estimatedTime }}</div>
							<div class="test-stat-card__label">Примерное время</div>
						</div>
					</div>
				</div>

				<!-- ===================== -->
				<!-- TAB: ПРЕДПРОСМОТР -->
				<!-- ===================== -->
				<div v-show="activeTab === 'preview'" class="test-tab-content">
					<div class="test-preview">
						<div class="test-preview__header">
							<h3 class="test-preview__title">
								{{ testName || 'Предпросмотр теста' }}
							</h3>
							<p v-if="testDescription" class="test-preview__description">
								{{ testDescription }}
							</p>
						</div>

						<div class="test-preview__info-grid">
							<div class="test-preview__info-item">
								<span class="test-preview__info-label">Вопросов:</span>
								<span class="test-preview__info-value">{{
									questions.length
								}}</span>
							</div>
							<div class="test-preview__info-item">
								<span class="test-preview__info-label">Баллов:</span>
								<span class="test-preview__info-value">{{ totalPoints }}</span>
							</div>
							<div v-if="duration" class="test-preview__info-item">
								<span class="test-preview__info-label">Время:</span>
								<span class="test-preview__info-value">{{ duration }} мин</span>
							</div>
							<div class="test-preview__info-item">
								<span class="test-preview__info-label">Проходной балл:</span>
								<span class="test-preview__info-value"
									>{{ passingScore }}%</span
								>
							</div>
							<div class="test-preview__info-item">
								<span class="test-preview__info-label">Попыток:</span>
								<span class="test-preview__info-value">{{ attempts }}</span>
							</div>
						</div>

						<div
							v-if="questions.length === 0"
							class="test-empty-state test-empty-state--small"
						>
							<div class="test-empty-state__icon">📝</div>
							<p class="test-empty-state__text">
								Добавьте вопросы для просмотра теста
							</p>
						</div>

						<div v-else class="test-preview__questions">
							<div
								v-for="(question, index) in questions"
								:key="question.id"
								class="test-preview-question"
							>
								<div class="test-preview-question__header">
									<span class="test-preview-question__number">
										Вопрос {{ index + 1 }}
									</span>
									<span class="test-preview-question__points">
										{{ question.customPoints || pointsPerQuestion }}
										{{
											pluralize(
												question.customPoints || pointsPerQuestion,
												'балл',
												'балла',
												'баллов'
											)
										}}
									</span>
								</div>

								<div class="test-preview-question__text">
									{{ question.text || 'Без текста' }}
								</div>

								<div v-if="question.image" class="test-preview-question__image">
									<img :src="question.image" alt="Question" />
								</div>

								<span class="test-preview-question__type">
									{{ getQuestionTypeLabel(question.type) }}
								</span>

								<!-- Варианты ответов -->
								<div
									v-if="
										question.type === 'single' || question.type === 'multiple'
									"
									class="test-preview-answers"
								>
									<div
										v-for="(answer, aIndex) in question.answers"
										:key="answer.id"
										class="test-preview-answer"
										:class="{
											'test-preview-answer--correct': answer.isCorrect,
										}"
									>
										<span class="test-preview-answer__marker">
											{{ String.fromCharCode(65 + aIndex) }}.
										</span>
										<span class="test-preview-answer__text">{{
											answer.text
										}}</span>
										<span
											v-if="answer.isCorrect"
											class="test-preview-answer__badge"
											>✓</span
										>
										<div v-if="answer.image" class="test-preview-answer__image">
											<img :src="answer.image" alt="Answer" />
										</div>
									</div>
								</div>

								<div
									v-if="question.type === 'text'"
									class="test-preview-question__correct-text"
								>
									<strong>Правильный ответ:</strong> {{ question.correctText }}
									<span
										v-if="question.caseSensitive"
										class="test-preview-question__badge"
									>
										С учетом регистра
									</span>
								</div>

								<div
									v-if="question.explanation"
									class="test-preview-question__explanation"
								>
									<strong>Объяснение:</strong> {{ question.explanation }}
								</div>
							</div>
						</div>

						<div v-if="questions.length > 0" class="test-preview__actions">
							<button
								type="button"
								class="test-btn test-btn--primary test-btn--large"
								@click="startTestPreview"
							>
								<span>Начать тест (пробное прохождение)</span>
							</button>
						</div>
					</div>
				</div>
			</div>

			<!-- Кнопки действий -->
			<div
				class="test-actions"
				style="display: flex; justify-content: flex-end; gap: 12px"
			>
				<span
					class="test-actions__preview-tooltip"
					:title="testPreviewDisabledHint"
				>
					<button
						type="button"
						class="test-actions__button test-actions__button--ghost"
						:disabled="!canOpenTestPreview || isSaving"
						@click="openTestPreviewInNewTab"
					>
						Предпросмотр теста
					</button>
				</span>
				<button
					v-if="false"
					type="button"
					class="test-actions__button test-actions__button--secondary"
					@click="handleSaveDraft"
					:disabled="isSaving"
				>
					Сохранить как черновик
				</button>

				<button
					type="button"
					class="test-actions__button test-actions__button--primary"
					:class="{ 'test-actions__button--disabled': isSaveButtonDisabled }"
					@click="handleCreate"
					:disabled="isSaveButtonDisabled || isSaving"
					:title="validationMessage"
				>
					<span v-if="isSaving" class="test-actions__spinner"></span>
					{{ saveButtonText }}
				</button>

        
			</div>
		</div>

		<!-- Скрытый input для загрузки файлов -->
		<input
			ref="fileInput"
			type="file"
			accept="image/*"
			style="display: none"
			@change="handleFileUpload"
		/>

		<!-- Confirm Dialog -->
		<div v-if="showConfirm" class="test-modal-overlay" @click="cancelConfirm">
			<div class="test-modal" @click.stop>
				<div class="test-modal__header">
					<h3 class="test-modal__title">{{ confirmData.title }}</h3>
				</div>
				<div class="test-modal__body">
					<p>{{ confirmData.message }}</p>
				</div>
				<div class="test-modal__footer">
					<button
						type="button"
						class="test-btn test-btn--secondary"
						@click="cancelConfirm"
					>
						{{ confirmData.cancelText || 'Отмена' }}
					</button>
					<button
						type="button"
						class="test-btn test-btn--danger"
						@click="confirmAction"
					>
						{{ confirmData.confirmText || 'Подтвердить' }}
					</button>
				</div>
			</div>
		</div>

		<!-- DOCX Import Dialog -->
		<div
			v-if="showDocxImportModal"
			class="test-modal-overlay"
			@click="closeDocxImportModal"
		>
			<div class="test-modal" @click.stop>
				<div class="test-modal__header">
					<h3 class="test-modal__title">Импорт вопросов из DOCX</h3>
				</div>
				<div class="test-modal__body">
					<p style="margin-bottom: 10px">
						Документ должен содержать блок <b>«Вопросы теста:»</b> и далее вопросы в
						следующем виде:
					</p>
					<pre class="docx-import__example">
Вопросы теста:
Какой элемент повышает прочность стали?
A) Кремний
B) Хром
C) Никель
Правильный ответ: B) Хром
Объяснение: Хром повышает закаливаемость и жаростойкость.
					</pre>
					<p style="margin-top: 10px">
						Поддерживается и вариант, когда <b>«Правильный ответ:»</b> и ответ идут
						на следующей строке.
					</p>
				</div>
				<div class="test-modal__footer">
					<button
						type="button"
						class="test-btn test-btn--secondary"
						@click="closeDocxImportModal"
					>
						Отмена
					</button>
					<button
						type="button"
						class="test-btn test-btn--danger"
						@click="handleImportQuestionsDocx"
					>
						Выбрать файл .docx
					</button>
				</div>
			</div>
		</div>

		<!-- Test Runner -->
		<TestRunner
			v-if="showTestRunner"
			:test-data="{
				title: testName || 'Пробный тест',
				description: testDescription,
				duration: duration,
				passingScore: passingScore,
				attempts: attempts,
				pointsPerQuestion: pointsPerQuestion,
				totalPoints: totalPoints,
				// Все настройки
				randomOrder: randomOrder,
				showCorrectAnswers: showCorrectAnswers,
				allowReview: allowReview,
				penalizeWrong: penalizeWrong,
				penaltyPercentage: penaltyPercentage,
				disableCopying: disableCopying,
				limitTimePerQuestion: limitTimePerQuestion,
				timePerQuestion: timePerQuestion,

				selectRandomQuestions: selectRandomQuestions,
				questionsToShow: questionsToShow,
				questionDistributionMode: questionDistributionMode,
				questionTypeDistribution: questionTypeDistribution,
			}"
			:questions="questions"
			@close="closeTestRunner"
		/>
	</div>
</template>

<script>
import AutoPublishSettings from './test-creation/AutoPublishSettings.vue'
import TestRunner from './test-creation/TestRunner.vue'
import { testsService } from '@/services'
import JSZip from 'jszip'
import { openInternalRouteInContext } from '@/utils/bitrixInternalNavigation'

const IMAGE_MAX_SIZE_MB = 5
const IMAGE_MAX_SIZE_BYTES = IMAGE_MAX_SIZE_MB * 1024 * 1024

export default {
	name: 'TestCreationForm',

	components: {
		AutoPublishSettings,
		TestRunner,
	},

	props: {
		currentModuleId: {
			type: [String, Number],
			default: null,
		},
		editingTest: {
			type: Object,
			default: null,
		},
	},

	emits: ['save-test', 'save-draft', 'cancel'],

	data() {
		return {
			// Редактирование
			editingTestId: null,

			// Табы
			activeTab: 'settings',

			// Автосохранение
			lastSaved: null,
			autoSaveInterval: null,
			autoSaveTimeout: null,
			storageKey: 'test_form_autosave',

			// Автопубликация
			isScheduledPublish: false,
			publishDate: '',
			publishTime: '',

			// Основные параметры
			testName: '',
			testDescription: '',
			selectedModule: '',
			category: '',
			duration: null,
			passingScore: 70,
			attempts: 1,
			pointsPerQuestion: 1,

			// Опции
			randomOrder: false,
			showCorrectAnswers: true,
			allowReview: true,
			penalizeWrong: false,
			penaltyPercentage: 50,

			disableCopying: false,
			limitTimePerQuestion: false,
			timePerQuestion: 60, // секунд по умолчанию

			// Вопросы
			questions: [],
			questionSearch: '',
			nextQuestionId: 1,
			nextAnswerId: 1,
			allExpanded: false,
			showErrors: false,

			// Типы вопросов
			questionTypes: [
				{
					value: 'single',
					label: 'Один ответ',
					description: 'Выбор одного правильного варианта',
					icon: '◉',
				},
				{
					value: 'multiple',
					label: 'Несколько ответов',
					description: 'Выбор нескольких правильных вариантов',
					icon: '☑',
				},
				{
					value: 'text',
					label: 'Текстовый ответ',
					description: 'Ввод текста',
					icon: '✎',
				},
			],

			// Валидация
			errors: {},

			// UI состояния
			isSaving: false,
			isUploading: false,
			uploadProgress: 0,
			currentUploadTarget: null,

			// Confirm dialog
			showConfirm: false,
			confirmData: {},

			// Импорт DOCX (инструкция/выбор файла)
			showDocxImportModal: false,

			// Drag & Drop
			draggedQuestionIndex: null,
			dragOverQuestionIndex: null,
			draggedAnswerData: null,
			dragOverAnswerIndex: null,

			// Test Runner
			showTestRunner: false,

			// Уведомления
			notifications: [],
			notificationId: 0,

			// Константы
			IMAGE_MAX_SIZE_MB,

			// ========== НОВОЕ: Настройки выбора вопросов ==========
			selectRandomQuestions: false,
			questionsToShow: null, // null = все вопросы
			questionDistributionMode: 'random', // 'random' | 'proportional' | 'fixed'
			questionTypeDistribution: {
				single: 0,
				multiple: 0,
				text: 0,
			},

			// Флаг сохранения (для предотвращения ложных предупреждений)
			isSaved: false,
			isLoadingData: false, // Флаг загрузки данных (блокирует watchers)
			originalData: null, // Снимок исходного состояния
			isOffline: typeof navigator !== 'undefined' ? !navigator.onLine : false,
			pendingQueueCount: 0,
			submitQueueKey: 'test_form_submit_queue_v1',
		}
	},

	computed: {
		distributionPercentageSum() {
			return (
				(this.questionTypeDistribution.single || 0) +
				(this.questionTypeDistribution.multiple || 0) +
				(this.questionTypeDistribution.text || 0)
			)
		},

		distributionFixedSum() {
			return (
				(this.questionTypeDistribution.single || 0) +
				(this.questionTypeDistribution.multiple || 0) +
				(this.questionTypeDistribution.text || 0)
			)
		},

		// ========== НОВОЕ: Статистика по типам вопросов ==========
		questionsByType() {
			return {
				single: this.questions.filter(q => q.type === 'single'),
				multiple: this.questions.filter(q => q.type === 'multiple'),
				text: this.questions.filter(q => q.type === 'text'),
			}
		},

		questionCountByType() {
			return {
				single: this.questionsByType.single.length,
				multiple: this.questionsByType.multiple.length,
				text: this.questionsByType.text.length,
			}
		},

		// Эффективное количество вопросов в тесте
		effectiveQuestionsCount() {
			if (!this.selectRandomQuestions || !this.questionsToShow) {
				return this.questions.length
			}
			return Math.min(this.questionsToShow, this.questions.length)
		},

		// Валидация распределения
		distributionValidation() {
			if (!this.selectRandomQuestions) {
				return { valid: true, message: '' }
			}

			const targetCount = this.effectiveQuestionsCount

			if (this.questionDistributionMode === 'random') {
				return { valid: true, message: 'Вопросы будут выбраны случайно' }
			}

			if (this.questionDistributionMode === 'proportional') {
				const sum =
					(this.questionTypeDistribution.single || 0) +
					(this.questionTypeDistribution.multiple || 0) +
					(this.questionTypeDistribution.text || 0)

				if (sum !== 100 && sum !== 0) {
					return {
						valid: false,
						message: `Сумма процентов должна быть 100% (сейчас ${sum}%)`,
					}
				}

				// Проверяем, хватит ли вопросов каждого типа
				const warnings = []
				Object.keys(this.questionTypeDistribution).forEach(type => {
					const percentage = this.questionTypeDistribution[type] || 0
					const needed = Math.ceil((targetCount * percentage) / 100)
					const available = this.questionCountByType[type]

					if (needed > available) {
						warnings.push(
							`${this.getQuestionTypeLabel(
								type
							)}: нужно ${needed}, доступно ${available}`
						)
					}
				})

				if (warnings.length > 0) {
					return {
						valid: false,
						message: 'Недостаточно вопросов: ' + warnings.join('; '),
					}
				}

				return { valid: true, message: 'Распределение корректно' }
			}

			if (this.questionDistributionMode === 'fixed') {
				const totalFixed =
					(this.questionTypeDistribution.single || 0) +
					(this.questionTypeDistribution.multiple || 0) +
					(this.questionTypeDistribution.text || 0)

				if (totalFixed > targetCount) {
					return {
						valid: false,
						message: `Сумма (${totalFixed}) превышает количество вопросов (${targetCount})`,
					}
				}

				// Проверяем доступность
				const warnings = []
				Object.keys(this.questionTypeDistribution).forEach(type => {
					const needed = this.questionTypeDistribution[type] || 0
					const available = this.questionCountByType[type]

					if (needed > available) {
						warnings.push(
							`${this.getQuestionTypeLabel(
								type
							)}: нужно ${needed}, доступно ${available}`
						)
					}
				})

				if (warnings.length > 0) {
					return {
						valid: false,
						message: 'Недостаточно вопросов: ' + warnings.join('; '),
					}
				}

				const remaining = targetCount - totalFixed
				const message =
					remaining > 0
						? `Указано ${totalFixed}, ещё ${remaining} будут выбраны случайно`
						: 'Распределение полное'

				return { valid: true, message }
			}

			return { valid: true, message: '' }
		},

		isDistributionValid() {
			return this.distributionValidation.valid
		},

		isEditMode() {
			return this.editingTestId !== null
		},

		tabs() {
			return [
				{ id: 'settings', label: 'Настройки', icon: '⚙️' },
				{
					id: 'questions',
					label: 'Вопросы',
					icon: '❓',
					badge: this.questions.length || null,
				},
				{ id: 'preview', label: 'Пробное прохождение', icon: '👁️' },
			]
		},

		lastSaveTimeFormatted() {
			if (!this.lastSaved) return null
			const date = new Date(this.lastSaved)
			return date.toLocaleTimeString('ru-RU', {
				hour: '2-digit',
				minute: '2-digit',
			})
		},

		// Прогресс заполнения формы
		formProgress() {
			let completed = 0
			const total = 5

			if (this.testName.trim()) completed++
			if (this.selectedModule) completed++
			if (this.questions.length > 0) completed++
			if (
				this.questions.length > 0 &&
				this.questions.every(q => q.text?.trim())
			)
				completed++
			if (
				this.questions.length > 0 &&
				this.questions.every(q => this.hasCorrectAnswer(q))
			)
				completed++

			return Math.round((completed / total) * 100)
		},

		isFormValid() {
			if (!this.testName.trim()) return false
			if (!this.selectedModule) return false
			if (this.questions.length === 0) return false

			return this.questions.every(q => {
				if (!q.text?.trim()) return false

				if (q.type === 'single' || q.type === 'multiple') {
					if (!q.answers || q.answers.length < 2) return false
					if (!q.answers.some(a => a.isCorrect)) return false
					if (!q.answers.every(a => a.text?.trim())) return false
				}

				if (q.type === 'text') {
					if (!q.correctText?.trim()) return false
				}

				return true
			})
		},

		validationMessage() {
			const errors = []

			if (!this.testName.trim()) errors.push('❌ Введите название теста')
			if (!this.selectedModule) errors.push('❌ Выберите модуль')

			if (this.questions.length === 0) {
				errors.push('❌ Добавьте хотя бы один вопрос')
			} else {
				this.questions.forEach((q, index) => {
					const qErrors = []

					if (!q.text?.trim()) qErrors.push('текст не заполнен')

					if (q.type === 'single' || q.type === 'multiple') {
						if (!q.answers || q.answers.length < 2) {
							qErrors.push('мин. 2 варианта')
						} else {
							if (!q.answers.some(a => a.isCorrect))
								qErrors.push('нет правильного ответа')

							const emptyCount = q.answers.filter(a => !a.text?.trim()).length
							if (emptyCount > 0) qErrors.push(`${emptyCount} пустых варианта`)
						}
					}

					if (q.type === 'text' && !q.correctText?.trim()) {
						qErrors.push('нет правильного ответа')
					}

					if (qErrors.length > 0) {
						errors.push(`❌ Вопрос ${index + 1}: ${qErrors.join(', ')}`)
					}
				})
			}

			return errors.length === 0
				? '✅ Все поля заполнены корректно!'
				: '⚠️ Необходимо исправить:\n\n' + errors.join('\n')
		},

		// Общие баллы с учётом кастомных баллов за вопросы
		totalPoints() {
			return this.questions.reduce((sum, q) => {
				return sum + (q.customPoints || this.pointsPerQuestion)
			}, 0)
		},

		// Минимальный проходной балл в абсолютных значениях
		passingPoints() {
			return Math.ceil((this.totalPoints * this.passingScore) / 100)
		},

		estimatedTime() {
			if (this.duration) return `${this.duration} мин`
			// ~1.5 минуты на вопрос
			const minutes = Math.ceil(this.questions.length * 1.5)
			return `~${minutes} мин`
		},

		// Фильтрация вопросов по поиску
		filteredQuestions() {
			if (!this.questionSearch.trim()) {
				return this.questions.filter(q => q != null)
			}

			const search = this.questionSearch.toLowerCase().trim()
			return this.questions.filter(q => {
				if (!q) return false

				// Поиск по тексту вопроса
				if (q.text?.toLowerCase().includes(search)) return true

				// Поиск по тегам
				if (q.tags?.toLowerCase().includes(search)) return true

				// Поиск по ответам
				if (q.answers?.some(a => a.text?.toLowerCase().includes(search)))
					return true

				// Поиск по объяснению
				if (q.explanation?.toLowerCase().includes(search)) return true

				return false
			})
		},

		// Статистика по типам вопросов
		questionTypeStats() {
			const stats = { single: 0, multiple: 0, text: 0 }
			this.questions.forEach(q => {
				if (stats[q.type] !== undefined) {
					stats[q.type]++
				}
			})
			return stats
		},

    // 🔥 ПЕРЕНОСИМ hasUnsavedChanges в computed
    hasUnsavedChanges() {
        // Если форма была успешно сохранена — изменений нет
        if (this.isSaved) {
            return false
        }

        // Если нет снимка исходных данных — значит это создание нового теста
        if (!this.originalData) {
            return !!(
                this.testName.trim() ||
                this.testDescription.trim() ||
                this.questions.length > 0
            )
        }

        // Сравниваем текущее состояние с исходным
        if (this.testName !== this.originalData.testName) return true
        if (this.testDescription !== this.originalData.testDescription) return true
        if (this.selectedModule !== this.originalData.selectedModule) return true
        if (this.category !== this.originalData.category) return true
        if (this.duration !== this.originalData.duration) return true
        if (this.passingScore !== this.originalData.passingScore) return true
        if (this.attempts !== this.originalData.attempts) return true
        if (this.pointsPerQuestion !== this.originalData.pointsPerQuestion) return true
        if (this.randomOrder !== this.originalData.randomOrder) return true
        if (this.showCorrectAnswers !== this.originalData.showCorrectAnswers) return true
        if (this.allowReview !== this.originalData.allowReview) return true
        if (this.penalizeWrong !== this.originalData.penalizeWrong) return true
        if (this.penaltyPercentage !== this.originalData.penaltyPercentage) return true
        if (this.disableCopying !== this.originalData.disableCopying) return true
        if (this.limitTimePerQuestion !== this.originalData.limitTimePerQuestion) return true
        if (this.timePerQuestion !== this.originalData.timePerQuestion) return true
        if (this.selectRandomQuestions !== this.originalData.selectRandomQuestions) return true
        if (this.questionsToShow !== this.originalData.questionsToShow) return true
        if (this.questionDistributionMode !== this.originalData.questionDistributionMode) return true
        if (this.isScheduledPublish !== this.originalData.isScheduledPublish) return true
        if (this.publishDate !== this.originalData.publishDate) return true
        if (this.publishTime !== this.originalData.publishTime) return true

        // Сравнение questionTypeDistribution
        const currentDistribution = JSON.stringify(this.questionTypeDistribution)
        if (currentDistribution !== this.originalData.questionTypeDistribution) return true

        // Сравнение вопросов
        const currentQuestions = JSON.stringify(
            this.questions.map(q => ({
                id: q.id,
                text: q.text,
                type: q.type,
                answers: q.answers?.map(a => ({
                    id: a.id,
                    text: a.text,
                    isCorrect: a.isCorrect,
                    image: a.image,
                })),
                correctText: q.correctText,
                caseSensitive: q.caseSensitive,
                explanation: q.explanation,
                customPoints: q.customPoints,
                image: q.image,
            }))
        )
        if (currentQuestions !== this.originalData.questions) return true

        return false
    },



		// 🔥 НОВЫЕ computed свойства для динамической кнопки
    saveButtonText() {
        if (this.isEditMode) {
            // БЕЗ скобок - это теперь computed!
            return this.hasUnsavedChanges ? 'Применить изменения' : 'Сохранено'
        }
        return 'Создать тест'
    },

    hasOnlyAdditionalSettingsChanges() {
        if (!this.originalData) return false

        const settingsChanged =
            this.randomOrder !== this.originalData.randomOrder ||
            this.showCorrectAnswers !== this.originalData.showCorrectAnswers ||
            this.allowReview !== this.originalData.allowReview ||
            this.penalizeWrong !== this.originalData.penalizeWrong ||
            this.penaltyPercentage !== this.originalData.penaltyPercentage ||
            this.disableCopying !== this.originalData.disableCopying ||
            this.limitTimePerQuestion !== this.originalData.limitTimePerQuestion ||
            this.timePerQuestion !== this.originalData.timePerQuestion ||
            this.selectRandomQuestions !== this.originalData.selectRandomQuestions ||
            this.questionsToShow !== this.originalData.questionsToShow ||
            this.questionDistributionMode !== this.originalData.questionDistributionMode ||
            JSON.stringify(this.questionTypeDistribution) !==
                this.originalData.questionTypeDistribution

        if (!settingsChanged) return false

        const nonSettingsChanged =
            this.testName !== this.originalData.testName ||
            this.testDescription !== this.originalData.testDescription ||
            this.selectedModule !== this.originalData.selectedModule ||
            this.category !== this.originalData.category ||
            this.duration !== this.originalData.duration ||
            this.passingScore !== this.originalData.passingScore ||
            this.attempts !== this.originalData.attempts ||
            this.pointsPerQuestion !== this.originalData.pointsPerQuestion ||
            this.isScheduledPublish !== this.originalData.isScheduledPublish ||
            this.publishDate !== this.originalData.publishDate ||
            this.publishTime !== this.originalData.publishTime ||
            JSON.stringify(
                this.questions.map(q => ({
                    id: q.id,
                    text: q.text,
                    type: q.type,
                    answers: q.answers?.map(a => ({
                        id: a.id,
                        text: a.text,
                        isCorrect: a.isCorrect,
                    })),
                    correctText: q.correctText,
                    caseSensitive: q.caseSensitive,
                    explanation: q.explanation,
                    customPoints: q.customPoints,
                }))
            ) !== this.originalData.questions

        return !nonSettingsChanged
    },

    isSaveButtonDisabled() {
        if (this.isEditMode) {
            // БЕЗ скобок!
            if (!this.hasUnsavedChanges) return true
            if (this.isFormValid) return false
            return !this.hasOnlyAdditionalSettingsChanges
        }
        return !this.isFormValid
    },
	canOpenTestPreview() {
		const moduleId = this.currentModuleId || this.selectedModule
		return (
			moduleId !== null &&
			moduleId !== undefined &&
			moduleId !== '' &&
			this.editingTestId !== null &&
			this.editingTestId !== undefined &&
			this.editingTestId !== '' &&
			!String(moduleId).startsWith('temp_') &&
			!String(this.editingTestId).startsWith('temp_')
		)
	},
	testPreviewDisabledHint() {
		if (this.isSaving) return ''
		return this.canOpenTestPreview
			? ''
			: 'Сначала создайте тест, чтобы открыть предпросмотр.'
	},
	},

	watch: {
		editingTest: {
			immediate: true,
			handler(newValue) {
				if (newValue) {
					this.loadTestData(newValue)
				}
			},
		},

		// 🔥 ОБНОВЛЕННЫЕ watchers с проверкой isLoadingData
		testName() {
			if (!this.isLoadingData) {
				this.scheduleAutoSave()
				this.clearFieldError('testName')
				this.isSaved = false
			}
		},

		testDescription() {
			if (!this.isLoadingData) {
				this.scheduleAutoSave()
				this.isSaved = false
			}
		},

		questions: {
			handler() {
				if (!this.isLoadingData) {
					this.scheduleAutoSave()
					this.isSaved = false
				}
			},
			deep: true,
		},

		// Добавляем watchers для остальных полей
		duration() {
			if (!this.isLoadingData) {
				this.scheduleAutoSave()
				this.isSaved = false
			}
		},

		passingScore() {
			if (!this.isLoadingData) {
				this.scheduleAutoSave()
				this.isSaved = false
			}
		},

		attempts() {
			if (!this.isLoadingData) {
				this.scheduleAutoSave()
				this.isSaved = false
			}
		},

		pointsPerQuestion(_newVal) {
			if (!this.isLoadingData) {
				this.isSaved = false
				// Обновляем баллы для вопросов без кастомных баллов
				this.questions.forEach(q => {
					if (!q.customPoints) {
						q._pointsUpdated = Date.now()
					}
				})
			}
		},

		randomOrder() {
			if (!this.isLoadingData) {
				this.scheduleAutoSave()
				this.isSaved = false
			}
		},

		showCorrectAnswers() {
			if (!this.isLoadingData) {
				this.scheduleAutoSave()
				this.isSaved = false
			}
		},

		allowReview() {
			if (!this.isLoadingData) {
				this.scheduleAutoSave()
				this.isSaved = false
			}
		},

		penalizeWrong() {
			if (!this.isLoadingData) {
				this.scheduleAutoSave()
				this.isSaved = false
			}
		},

		penaltyPercentage() {
			if (!this.isLoadingData) {
				this.scheduleAutoSave()
				this.isSaved = false
			}
		},

		disableCopying() {
			if (!this.isLoadingData) {
				this.scheduleAutoSave()
				this.isSaved = false
			}
		},

		limitTimePerQuestion() {
			if (!this.isLoadingData) {
				this.scheduleAutoSave()
				this.isSaved = false
			}
		},

		timePerQuestion() {
			if (!this.isLoadingData) {
				this.scheduleAutoSave()
				this.isSaved = false
			}
		},

		selectRandomQuestions() {
			if (!this.isLoadingData) {
				this.scheduleAutoSave()
				this.isSaved = false
			}
		},

		questionsToShow() {
			if (!this.isLoadingData) {
				this.scheduleAutoSave()
				this.isSaved = false
			}
		},

		questionDistributionMode() {
			if (!this.isLoadingData) {
				this.scheduleAutoSave()
				this.isSaved = false
			}
		},

		questionTypeDistribution: {
			handler() {
				if (!this.isLoadingData) {
					this.scheduleAutoSave()
					this.isSaved = false
				}
			},
			deep: true,
		},

		isScheduledPublish() {
			if (!this.isLoadingData) {
				this.scheduleAutoSave()
				this.isSaved = false
			}
		},

		publishDate() {
			if (!this.isLoadingData) {
				this.scheduleAutoSave()
				this.isSaved = false
			}
		},

		publishTime() {
			if (!this.isLoadingData) {
				this.scheduleAutoSave()
				this.isSaved = false
			}
		},

		currentModuleId(newId, oldId) {
			if (newId && newId !== oldId) {
				this.selectedModule = newId
				if (newId >= 1000000000000) {
					this.clearLocalStorage()
				}
			}
		},
	},

	mounted() {
		if (this.editingTest) {
			this.loadTestData(this.editingTest)
		}

		if (this.currentModuleId && !this.selectedModule) {
			this.selectedModule = this.currentModuleId
		}

		const isNewModule =
			this.currentModuleId && this.currentModuleId >= 1000000000000
		if (isNewModule) {
			this.clearLocalStorage()
		} else {
			this.loadFromLocalStorage()
		}

		this.setupAutoSave()
		// Добавляем слушатели для drag & drop
		this.setupDragAndDropListeners()
		window.addEventListener('beforeunload', this.handleBeforeUnload)
		this.pendingQueueCount = this.getSubmitQueue().length
		window.addEventListener('online', this.handleOnline)
		window.addEventListener('offline', this.handleOffline)
		if (!this.isOffline) {
			this.flushSubmitQueue()
		}
	},

	beforeUnmount() {
		if (this.autoSaveInterval) {
			clearInterval(this.autoSaveInterval)
		}

		if (this.autoSaveTimeout) {
			clearTimeout(this.autoSaveTimeout)
		}

		this.removeDragAndDropListeners()
		window.removeEventListener('beforeunload', this.handleBeforeUnload)
		window.removeEventListener('online', this.handleOnline)
		window.removeEventListener('offline', this.handleOffline)
	},

	methods: {
		handleOnline() {
			this.isOffline = false
			this.flushSubmitQueue()
		},
		handleOffline() {
			this.isOffline = true
		},
		getSubmitQueue() {
			try {
				const raw = localStorage.getItem(this.submitQueueKey)
				const parsed = raw ? JSON.parse(raw) : []
				return Array.isArray(parsed) ? parsed : []
			} catch (error) {
				console.error('Ошибка чтения очереди отправки теста:', error)
				return []
			}
		},
		setSubmitQueue(queue) {
			try {
				localStorage.setItem(this.submitQueueKey, JSON.stringify(queue))
			} catch (error) {
				console.error('Ошибка записи очереди отправки теста:', error)
			}
			this.pendingQueueCount = Array.isArray(queue) ? queue.length : 0
		},
		enqueueSubmit(eventName, payload) {
			const queue = this.getSubmitQueue()
			queue.push({ eventName, payload, createdAt: Date.now() })
			this.setSubmitQueue(queue)
		},
		flushSubmitQueue() {
			if (typeof navigator !== 'undefined' && !navigator.onLine) return
			const queue = this.getSubmitQueue()
			if (!queue.length) return
			queue.forEach(item => {
				if (item && item.eventName && item.payload) {
					this.$emit(item.eventName, item.payload)
				}
			})
			this.setSubmitQueue([])
		},
		// ============================================
		// 🔥 НОВЫЕ МЕТОДЫ для отслеживания изменений
		// ============================================

		/**
		 * Захватывает текущее состояние формы как исходное
		 */
		captureOriginalData() {
			this.originalData = {
				testName: this.testName,
				testDescription: this.testDescription,
				selectedModule: this.selectedModule,
				category: this.category,
				duration: this.duration,
				passingScore: this.passingScore,
				attempts: this.attempts,
				pointsPerQuestion: this.pointsPerQuestion,
				randomOrder: this.randomOrder,
				showCorrectAnswers: this.showCorrectAnswers,
				allowReview: this.allowReview,
				penalizeWrong: this.penalizeWrong,
				penaltyPercentage: this.penaltyPercentage,
				disableCopying: this.disableCopying,
				limitTimePerQuestion: this.limitTimePerQuestion,
				timePerQuestion: this.timePerQuestion,
				selectRandomQuestions: this.selectRandomQuestions,
				questionsToShow: this.questionsToShow,
				questionDistributionMode: this.questionDistributionMode,
				questionTypeDistribution: JSON.stringify(this.questionTypeDistribution),
				isScheduledPublish: this.isScheduledPublish,
				publishDate: this.publishDate,
				publishTime: this.publishTime,
				// Для вопросов сохраняем сериализованную версию
				questions: JSON.stringify(
					this.questions.map(q => ({
						id: q.id,
						text: q.text,
						type: q.type,
						answers: q.answers?.map(a => ({
							id: a.id,
							text: a.text,
							isCorrect: a.isCorrect,
							image: a.image,
						})),
						correctText: q.correctText,
						caseSensitive: q.caseSensitive,
						explanation: q.explanation,
						customPoints: q.customPoints,
						image: q.image,
					}))
				),
			}
			console.log('📸 Снимок исходных данных теста сохранён')
		},

		// ============================================
		// УВЕДОМЛЕНИЯ
		// ============================================
		showNotification(message, type = 'info', duration = 3000) {
			const id = ++this.notificationId
			const notification = { id, message, type }
			this.notifications.push(notification)

			if (duration > 0) {
				setTimeout(() => {
					this.removeNotification(id)
				}, duration)
			}

			return id
		},

		removeNotification(id) {
			const index = this.notifications.findIndex(n => n.id === id)
			if (index !== -1) {
				this.notifications.splice(index, 1)
			}
		},

		// ============================================
		// DRAG & DROP LISTENERS
		// ============================================
		setupDragAndDropListeners() {
			// Слушатель для завершения перетаскивания вне целевой области
			document.addEventListener('dragend', this.handleGlobalDragEnd)
		},

		removeDragAndDropListeners() {
			document.removeEventListener('dragend', this.handleGlobalDragEnd)
		},

		handleGlobalDragEnd() {
			// Сбрасываем все состояния drag & drop при завершении перетаскивания
			this.draggedQuestionIndex = null
			this.dragOverQuestionIndex = null
			this.draggedAnswerData = null
			this.dragOverAnswerIndex = null
		},

		// ============================================
		// DRAG & DROP ДЛЯ ВОПРОСОВ
		// ============================================
		// Начало перетаскивания
		handleQuestionDragStart(event, index) {
			this.draggedQuestionIndex = index

			// Устанавливаем данные для переноса
			event.dataTransfer.effectAllowed = 'move'
			event.dataTransfer.setData('text/plain', index.toString())

			// Делаем элемент полупрозрачным
			event.target.style.opacity = '0.5'

			// Небольшая задержка для визуального эффекта
			setTimeout(() => {
				event.target.classList.add('test-question--dragging')
			}, 0)
		},

		// Элемент над зоной сброса
		handleQuestionDragOver(event, index) {
			event.preventDefault()
			event.dataTransfer.dropEffect = 'move'

			// Показываем индикатор только если это не тот же элемент
			if (this.draggedQuestionIndex !== index) {
				this.dragOverQuestionIndex = index
			}
		},

		// Выход из зоны сброса
		handleQuestionDragLeave(index) {
			if (this.dragOverQuestionIndex === index) {
				this.dragOverQuestionIndex = null
			}
		},

		// Сброс элемента
		handleQuestionDrop(event, targetIndex) {
			event.preventDefault()
			const sourceIndex = this.draggedQuestionIndex

			// Проверяем, что это не тот же элемент
			if (sourceIndex === null || sourceIndex === targetIndex) {
				this.resetDragState()
				return
			}

			// Перемещаем вопрос
			const questionToMove = this.questions[sourceIndex]

			// Удаляем из старой позиции
			this.questions.splice(sourceIndex, 1)

			// Вставляем в новую позицию
			// Корректируем индекс, если элемент перемещается вниз
			const adjustedIndex =
				sourceIndex < targetIndex ? targetIndex - 1 : targetIndex
			this.questions.splice(adjustedIndex, 0, questionToMove)

			console.log(
				`✅ Вопрос перемещён с позиции ${sourceIndex + 1} на позицию ${
					adjustedIndex + 1
				}`
			)

			// Сбрасываем состояние
			this.resetDragState()
		},

		// Окончание перетаскивания
		handleQuestionDragEnd(event) {
			event.target.style.opacity = '1'
			event.target.classList.remove('test-question--dragging')
			this.resetDragState()
		},

		// Сброс состояния drag & drop
		resetDragState() {
			this.draggedQuestionIndex = null
			this.dragOverQuestionIndex = null
		},

		// ============================================
		// КНОПКИ ПЕРЕМЕЩЕНИЯ ВВЕРХ/ВНИЗ
		// ============================================
		moveQuestionUp(index) {
			if (index <= 0) return
			this.swapQuestions(index, index - 1)
		},

		moveQuestionDown(index) {
			if (index >= this.questions.length - 1) return
			this.swapQuestions(index, index + 1)
		},

		swapQuestions(index1, index2) {
			const temp = this.questions[index1]
			this.questions.splice(index1, 1, this.questions[index2])
			this.questions.splice(index2, 1, temp)
		},

		// ============================================
		// DRAG & DROP ДЛЯ ОТВЕТОВ
		// ============================================
		handleAnswerDragStart(event, question, answerIndex) {
			this.draggedAnswerData = {
				questionId: question.id,
				answerIndex,
			}

			event.dataTransfer.effectAllowed = 'move'
			event.dataTransfer.setData('text/plain', answerIndex.toString())
			event.target.style.opacity = '0.5'
		},

		handleAnswerDragOver(event, question, answerIndex) {
			event.preventDefault()

			// Проверяем, что перетаскиваем ответ из того же вопроса
			if (this.draggedAnswerData?.questionId === question.id) {
				event.dataTransfer.dropEffect = 'move'
				this.dragOverAnswerIndex = answerIndex
			}
		},

		handleAnswerDragLeave(answerIndex) {
			if (this.dragOverAnswerIndex === answerIndex) {
				this.dragOverAnswerIndex = null
			}
		},

		handleAnswerDrop(event, question, targetIndex) {
			event.preventDefault()

			if (
				!this.draggedAnswerData ||
				this.draggedAnswerData.questionId !== question.id
			) {
				this.resetAnswerDragState()
				return
			}

			const sourceIndex = this.draggedAnswerData.answerIndex
			if (sourceIndex === targetIndex) {
				this.resetAnswerDragState()
				return
			}

			// Перемещаем ответ
			const answerToMove = question.answers[sourceIndex]
			question.answers.splice(sourceIndex, 1)

			const adjustedIndex =
				sourceIndex < targetIndex ? targetIndex - 1 : targetIndex
			question.answers.splice(adjustedIndex, 0, answerToMove)

			this.resetAnswerDragState()
		},

		handleAnswerDragEnd(event) {
			event.target.style.opacity = '1'
			this.resetAnswerDragState()
		},

		resetAnswerDragState() {
			this.draggedAnswerData = null
			this.dragOverAnswerIndex = null
		},

		// ============================================
		// ВАЛИДАЦИЯ
		// ============================================
		validateField(field) {
			switch (field) {
				case 'testName':
					if (!this.testName.trim()) {
						this.errors.testName = 'Название теста обязательно'
					} else if (this.testName.length > 200) {
						this.errors.testName = 'Максимум 200 символов'
					} else {
						delete this.errors.testName
					}
					break

				case 'selectedModule':
					if (!this.selectedModule) {
						this.errors.selectedModule = 'Выберите модуль'
					} else {
						delete this.errors.selectedModule
					}
					break

				case 'duration':
					if (
						this.duration !== null &&
						(this.duration < 1 || this.duration > 300)
					) {
						this.errors.duration = 'От 1 до 300 минут'
					} else {
						delete this.errors.duration
					}
					break

				case 'passingScore':
					if (this.passingScore < 0 || this.passingScore > 100) {
						this.errors.passingScore = 'От 0 до 100%'
					} else {
						delete this.errors.passingScore
					}
					break

				case 'attempts':
					if (this.attempts < 1 || this.attempts > 100) {
						this.errors.attempts = 'От 1 до 100'
					} else {
						delete this.errors.attempts
					}
					break
			}
		},

		clearFieldError(field) {
			if (this.errors[field]) {
				delete this.errors[field]
			}
		},

		hasQuestionErrors(question) {
			if (!this.showErrors) return false

			if (!question.text?.trim()) return true

			if (question.type === 'text' && !question.correctText?.trim()) return true

			if (question.type === 'single' || question.type === 'multiple') {
				if (!question.answers || question.answers.length < 2) return true
				if (!this.hasCorrectAnswer(question)) return true
				if (question.answers.some(a => !a.text?.trim())) return true
			}

			return false
		},

		getQuestionErrorsText(question) {
			const errors = []

			if (!question.text?.trim()) errors.push('Текст вопроса не заполнен')

			if (question.type === 'single' || question.type === 'multiple') {
				if (!question.answers || question.answers.length < 2) {
					errors.push('Добавьте минимум 2 варианта ответа')
				} else {
					if (!this.hasCorrectAnswer(question))
						errors.push('Не отмечен правильный ответ')

					const emptyCount = question.answers.filter(
						a => !a.text?.trim()
					).length
					if (emptyCount > 0)
						errors.push(`${emptyCount} вариант(а) не заполнены`)
				}
			}

			if (question.type === 'text' && !question.correctText?.trim()) {
				errors.push('Правильный ответ не указан')
			}

			return errors.join('\n')
		},

		hasCorrectAnswer(question) {
			if (question.type === 'text') return !!question.correctText?.trim()
			return question.answers?.some(a => a.isCorrect) || false
		},

		// Валидация конкретного вопроса
		validateQuestion(question) {
			const errors = []

			if (!question.text?.trim()) {
				errors.push({ field: 'text', message: 'Введите текст вопроса' })
			}

			if (question.type === 'single' || question.type === 'multiple') {
				if (!question.answers || question.answers.length < 2) {
					errors.push({
						field: 'answers',
						message: 'Минимум 2 варианта ответа',
					})
				} else {
					if (!question.answers.some(a => a.isCorrect)) {
						errors.push({
							field: 'correct',
							message: 'Выберите правильный ответ',
						})
					}

					question.answers.forEach((a, i) => {
						if (!a.text?.trim()) {
							errors.push({
								field: `answer_${i}`,
								message: `Заполните вариант ${i + 1}`,
							})
						}
					})
				}
			}

			if (question.type === 'text' && !question.correctText?.trim()) {
				errors.push({
					field: 'correctText',
					message: 'Введите правильный ответ',
				})
			}

			return errors
		},

		// ============================================
		// УПРАВЛЕНИЕ ВОПРОСАМИ
		// ============================================
		createQuestion(type = 'single') {
			return {
				id: this.nextQuestionId++,
				text: '',
				type,
				isPersisted: false,
				answers:
					type !== 'text'
						? [
								{
									id: this.nextAnswerId++,
									text: '',
									isCorrect: false,
									image: null,
									imageFile: null,
									isPersisted: false,
								},
								{
									id: this.nextAnswerId++,
									text: '',
									isCorrect: false,
									image: null,
									imageFile: null,
									isPersisted: false,
								},
						  ]
						: [],
				correctText: '',
				caseSensitive: false,
				explanation: '',
				tags: '',
				customPoints: null,
				image: null,
				imageFile: null,
				isExpanded: true,

				// Метаданные
				createdAt: new Date().toISOString(),
				updatedAt: new Date().toISOString(),
			}
		},

		handleAddQuestion(type = 'single') {
			const newQuestion = this.createQuestion(type)
			this.questions.push(newQuestion)

			if (this.questions.length === 1) {
				this.showErrors = true
			}

			if (this.activeTab !== 'questions') {
				this.activeTab = 'questions'
			}

			this.$nextTick(() => {
				const questionItems = document.querySelectorAll('.test-question')
				const lastItem = questionItems[questionItems.length - 1]
				lastItem?.scrollIntoView({ behavior: 'smooth', block: 'center' })

				// Фокус на поле текста вопроса
				const textarea = lastItem?.querySelector('.test-field__textarea')
				textarea?.focus()
			})

			this.showNotification('Вопрос добавлен', 'success')
		},

		handleRemoveQuestion(index) {
			const question = this.questions[index]

			this.showConfirmDialog({
				title: 'Удалить вопрос?',
				message: question.text
					? `Вы уверены, что хотите удалить вопрос "${this.truncate(
							question.text,
							50
					  )}"?`
					: 'Вы уверены, что хотите удалить этот вопрос?',
				confirmText: 'Удалить',
				cancelText: 'Отмена',
				onConfirm: () => {
					this.questions.splice(index, 1)
					this.showNotification('Вопрос удалён', 'success')
				},
			})
		},

		duplicateQuestion(index) {
			const original = this.questions[index]
			const duplicate = {
				...JSON.parse(JSON.stringify(original)), // Глубокое копирование
				id: this.nextQuestionId++,
				isExpanded: true,
				isPersisted: false,
				imageFile: null,
				createdAt: new Date().toISOString(),
				updatedAt: new Date().toISOString(),
			}

			// Обновляем ID ответов
			if (duplicate.answers) {
				duplicate.answers = duplicate.answers.map(a => ({
					...a,
					id: this.nextAnswerId++,
					isPersisted: false,
					imageFile: null,
				}))
			}

			this.questions.splice(index + 1, 0, duplicate)
			this.showNotification('Вопрос дублирован', 'success')

			this.$nextTick(() => {
				const questionItems = document.querySelectorAll('.test-question')
				const duplicatedItem = questionItems[index + 1]
				duplicatedItem?.scrollIntoView({ behavior: 'smooth', block: 'center' })
			})
		},

		toggleQuestion(questionId) {
			const question = this.questions.find(q => q.id === questionId)
			if (question) {
				question.isExpanded = !question.isExpanded
			}
		},

		toggleAllQuestions() {
			this.allExpanded = !this.allExpanded
			this.questions.forEach(q => {
				q.isExpanded = this.allExpanded
			})
		},

		// Перемещение вопроса вверх/вниз
		moveQuestion(index, direction) {
			const newIndex = index + direction
			if (newIndex < 0 || newIndex >= this.questions.length) return

			const temp = this.questions[index]
			this.questions.splice(index, 1)
			this.questions.splice(newIndex, 0, temp)
		},

		handleQuestionTypeChange(question) {
			const _oldType = question._previousType
			question._previousType = question.type

			if (question.type === 'text') {
				// Сохраняем ответы на случай, если пользователь вернётся
				question._savedAnswers = question.answers
				question.answers = []
				question.correctText = ''
			} else {
				// Восстанавливаем сохранённые ответы или создаём новые
				if (question._savedAnswers && question._savedAnswers.length >= 2) {
					question.answers = question._savedAnswers
				} else if (!question.answers || question.answers.length < 2) {
					question.answers = [
						{
							id: this.nextAnswerId++,
							text: '',
							isCorrect: false,
							image: null,
						},
						{
							id: this.nextAnswerId++,
							text: '',
							isCorrect: false,
							image: null,
						},
					]
				}
				question.correctText = ''

				// Для single - только один правильный ответ
				if (question.type === 'single') {
					const correctCount = question.answers.filter(a => a.isCorrect).length
					if (correctCount > 1) {
						// Оставляем только первый правильный
						let foundFirst = false
						question.answers.forEach(a => {
							if (a.isCorrect && !foundFirst) {
								foundFirst = true
							} else {
								a.isCorrect = false
							}
						})
					}
				}
			}

			question.updatedAt = new Date().toISOString()
		},

		// ============================================
		// УПРАВЛЕНИЕ ОТВЕТАМИ
		// ============================================
		addAnswer(question) {
			if (!question.answers) question.answers = []

			question.answers.push({
				id: this.nextAnswerId++,
				text: '',
				isCorrect: false,
				image: null,
				imageFile: null,
				isPersisted: false,
			})

			question.updatedAt = new Date().toISOString()

			this.$nextTick(() => {
				// Фокус на новом ответе
				const answers = document.querySelectorAll(
					`[data-question-id="${question.id}"] .test-answer__input`
				)
				const lastAnswer = answers[answers.length - 1]
				lastAnswer?.focus()
			})
		},

		removeAnswer(question, answerIndex) {
			if (!question.answers || question.answers.length <= 2) {
				this.showNotification('Минимум 2 варианта ответа', 'warning')
				return
			}
			if (!window.confirm('Удалить вариант ответа?')) {
				return
			}

			question.answers.splice(answerIndex, 1)
			question.updatedAt = new Date().toISOString()
		},

		setCorrectAnswer(question, answerIndex) {
			if (!question.answers) return

			if (question.type === 'single') {
				// Для single - только один правильный
				question.answers.forEach((answer, index) => {
					answer.isCorrect = index === answerIndex
				})
			} else {
				// Для multiple - переключаем состояние
				question.answers[answerIndex].isCorrect =
					!question.answers[answerIndex].isCorrect
			}

			question.updatedAt = new Date().toISOString()
		},

		// Массовые операции с ответами
		clearAllAnswers(question) {
			if (!question.answers) return
			if (!window.confirm('Очистить все ответы в этом вопросе?')) {
				return
			}

			question.answers.forEach(a => {
				a.text = ''
				a.isCorrect = false
				a.image = null
				a.imageFile = null
			})
		},

		shuffleAnswers(question) {
			if (!question.answers || question.answers.length < 2) return

			// Fisher-Yates shuffle
			for (let i = question.answers.length - 1; i > 0; i--) {
				const j = Math.floor(Math.random() * (i + 1))
				;[question.answers[i], question.answers[j]] = [
					question.answers[j],
					question.answers[i],
				]
			}

			this.showNotification('Ответы перемешаны', 'success')
		},

		// ============================================
		// ЗАГРУЗКА ИЗОБРАЖЕНИЙ
		// ============================================
		uploadQuestionImage(question) {
			this.currentUploadTarget = { type: 'question', target: question }
			this.$refs.fileInput?.click()
		},

		uploadAnswerImage(answer) {
			this.currentUploadTarget = { type: 'answer', target: answer }
			this.$refs.fileInput?.click()
		},

		handleFileUpload(event) {
			const file = event.target.files?.[0]
			if (!file) return

			// Валидация типа файла
			if (!file.type.startsWith('image/')) {
				this.showNotification('Можно загружать только изображения', 'error')
				event.target.value = ''
				return
			}

			// Валидация размера
			if (file.size > IMAGE_MAX_SIZE_BYTES) {
				this.showNotification(
					`Максимальный размер файла: ${IMAGE_MAX_SIZE_MB} МБ`,
					'error'
				)
				event.target.value = ''
				return
			}

			this.isUploading = true
			this.uploadProgress = 0
			if (
				this.currentUploadTarget?.target?.image &&
				!window.confirm('Заменить уже загруженное изображение?')
			) {
				this.currentUploadTarget = null
				event.target.value = ''
				this.isUploading = false
				this.uploadProgress = 0
				return
			}

			const reader = new FileReader()

			reader.onprogress = e => {
				if (e.lengthComputable) {
					this.uploadProgress = Math.round((e.loaded / e.total) * 100)
				}
			}

			reader.onload = e => {
				if (this.currentUploadTarget) {
					this.currentUploadTarget.target.image = e.target.result
					this.currentUploadTarget.target.imageFile = file

					// Обновляем updatedAt для вопроса
					if (this.currentUploadTarget.type === 'question') {
						this.currentUploadTarget.target.updatedAt = new Date().toISOString()
					}

					this.currentUploadTarget = null
					this.showNotification('Изображение загружено', 'success')
				}

				this.isUploading = false
				this.uploadProgress = 0
			}

			reader.onerror = () => {
				this.showNotification('Ошибка при загрузке изображения', 'error')
				this.isUploading = false
				this.uploadProgress = 0
				this.currentUploadTarget = null
			}

			reader.readAsDataURL(file)
			event.target.value = ''
		},

		async removeQuestionImage(question) {
			if (!window.confirm('Удалить изображение вопроса?')) {
				return
			}
			const imageUrl = question?.image
			question.image = null
			question.imageFile = null
			question.updatedAt = new Date().toISOString()

			if (
				this.isEditMode &&
				question?.isPersisted &&
				question?.id &&
				imageUrl &&
				!imageUrl.startsWith('data:')
			) {
				try {
					await testsService.deleteQuestionImage(question.id)
					this.showNotification('Изображение вопроса удалено', 'success')
				} catch (error) {
					console.error('Ошибка удаления изображения вопроса:', error)
					this.showNotification('Ошибка удаления изображения вопроса', 'error')
				}
			}
		},

		async removeAnswerImage(answer) {
			if (!window.confirm('Удалить изображение ответа?')) {
				return
			}
			const imageUrl = answer?.image
			answer.image = null
			answer.imageFile = null

			if (
				this.isEditMode &&
				answer?.isPersisted &&
				answer?.id &&
				imageUrl &&
				!imageUrl.startsWith('data:')
			) {
				try {
					await testsService.deleteAnswerImage(answer.id)
					this.showNotification('Изображение ответа удалено', 'success')
				} catch (error) {
					console.error('Ошибка удаления изображения ответа:', error)
					this.showNotification('Ошибка удаления изображения ответа', 'error')
				}
			}
		},

		// ============================================
		// СЛУЧАЙНЫЙ ПОРЯДОК (при сохранении/экспорте)
		// ============================================
		getShuffledQuestions() {
			if (!this.randomOrder) return [...this.questions]

			const shuffled = [...this.questions]
			for (let i = shuffled.length - 1; i > 0; i--) {
				const j = Math.floor(Math.random() * (i + 1))
				;[shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]]
			}

			return shuffled
		},

		// Перемешать ответы в вопросе
		shuffleQuestionAnswers(question) {
			if (!question.answers || question.answers.length < 2) return question

			const shuffledAnswers = [...question.answers]
			for (let i = shuffledAnswers.length - 1; i > 0; i--) {
				const j = Math.floor(Math.random() * (i + 1))
				;[shuffledAnswers[i], shuffledAnswers[j]] = [
					shuffledAnswers[j],
					shuffledAnswers[i],
				]
			}

			return { ...question, answers: shuffledAnswers }
		},

		// ============================================
		// РАСЧЁТ БАЛЛОВ
		// ============================================
		calculateQuestionPoints(question, userAnswer) {
			const basePoints = question.customPoints || this.pointsPerQuestion

			if (question.type === 'single') {
				const correctAnswer = question.answers?.find(a => a.isCorrect)
				if (userAnswer === correctAnswer?.id) {
					return basePoints
				}

				return this.penalizeWrong
					? -Math.round((basePoints * this.penaltyPercentage) / 100)
					: 0
			}

			if (question.type === 'multiple') {
				const correctIds =
					question.answers?.filter(a => a.isCorrect).map(a => a.id) || []
				const userIds = Array.isArray(userAnswer) ? userAnswer : []

				const correctSelected = userIds.filter(id =>
					correctIds.includes(id)
				).length
				const incorrectSelected = userIds.filter(
					id => !correctIds.includes(id)
				).length

				if (incorrectSelected > 0 && this.penalizeWrong) {
					const penalty = Math.round(
						(((basePoints * this.penaltyPercentage) / 100) *
							incorrectSelected) /
							correctIds.length
					)

					return Math.max(
						0,
						Math.round((basePoints * correctSelected) / correctIds.length) -
							penalty
					)
				}

				return Math.round((basePoints * correctSelected) / correctIds.length)
			}

			if (question.type === 'text') {
				const correct = question.caseSensitive
					? userAnswer === question.correctText
					: userAnswer?.toLowerCase() === question.correctText?.toLowerCase()

				if (correct) return basePoints

				return this.penalizeWrong
					? -Math.round((basePoints * this.penaltyPercentage) / 100)
					: 0
			}

			return 0
		},

		// ============================================
		// ИМПОРТ/ЭКСПОРТ
		// ============================================
		handleImportQuestionsJson() {
			const input = document.createElement('input')
			input.type = 'file'
			input.accept = '.json'

			input.onchange = e => {
				const file = e.target.files[0]
				if (!file) return

				const fileName = String(file.name || '').toLowerCase()
				if (!fileName.endsWith('.json')) {
					this.showNotification('Выберите файл .json', 'error')
					return
				}

				const reader = new FileReader()

				reader.onload = event => {
					try {
						const data = JSON.parse(event.target.result)
						let importedCount = 0

						// Импорт настроек теста
						if (data.test) {
							if (
								confirm('Импортировать настройки теста вместе с вопросами?')
							) {
								this.importTestSettings(data.test)
							}
						}

						// Импорт вопросов
						const questionsToImport =
							data.questions || (Array.isArray(data) ? data : [])

						// 🔥 Заменяем существующие вопросы вместо добавления
						const importedQuestions = []
						questionsToImport.forEach(q => {
							const importedQuestion = this.createQuestionFromImport(q)
							if (importedQuestion) {
								importedQuestions.push(importedQuestion)
								importedCount++
							}
						})

						if (importedCount > 0) {
							// 🔥 Полностью заменяем массив вопросов
							this.questions = importedQuestions
							// 🔥 Обновляем nextQuestionId
							this.nextQuestionId =
								Math.max(...this.questions.map(q => q.id || 0)) + 1
							// 🔥 Обновляем nextAnswerId
							const allAnswers = this.questions.flatMap(q => q.answers || [])
							if (allAnswers.length > 0) {
								this.nextAnswerId =
									Math.max(...allAnswers.map(a => a.id || 0)) + 1
							}

							this.showNotification(
								`Импортировано вопросов: ${importedCount} (заменены существующие)`,
								'success'
							)
							this.activeTab = 'questions'
						} else {
							this.showNotification(
								'Не найдено вопросов для импорта',
								'warning'
							)
						}
					} catch (error) {
						console.error('Import error:', error)
						this.showNotification(
							`Ошибка при импорте: ${error.message}`,
							'error'
						)
					}
				}

				reader.onerror = () => {
					this.showNotification('Ошибка при чтении файла', 'error')
				}

				reader.readAsText(file)
			}

			input.click()
		},

		openDocxImportModal() {
			this.showDocxImportModal = true
		},

		closeDocxImportModal() {
			this.showDocxImportModal = false
		},

		handleImportQuestionsDocx() {
			const input = document.createElement('input')
			input.type = 'file'
			input.accept = '.docx'

			input.onchange = e => {
				const file = e.target.files[0]
				if (!file) return

				const fileName = String(file.name || '').toLowerCase()
				if (!fileName.endsWith('.docx')) {
					this.showNotification('Выберите файл .docx', 'error')
					return
				}

				const reader = new FileReader()

				reader.onload = async event => {
					try {
						const data = await this.parseDOCX(event.target.result)

						let importedCount = 0

						// В .docx импортируем только вопросы (без настроек теста).

						// Импорт вопросов
						const questionsToImport =
							data.questions || (Array.isArray(data) ? data : [])

						// 🔥 Заменяем существующие вопросы вместо добавления
						const importedQuestions = []
						questionsToImport.forEach(q => {
							const importedQuestion = this.createQuestionFromImport(q)
							if (importedQuestion) {
								importedQuestions.push(importedQuestion)
								importedCount++
							}
						})

						if (importedCount > 0) {
							// 🔥 Полностью заменяем массив вопросов
							this.questions = importedQuestions
							// 🔥 Обновляем nextQuestionId
							this.nextQuestionId =
								Math.max(...this.questions.map(q => q.id || 0)) + 1
							// 🔥 Обновляем nextAnswerId
							const allAnswers = this.questions.flatMap(q => q.answers || [])
							if (allAnswers.length > 0) {
								this.nextAnswerId =
									Math.max(...allAnswers.map(a => a.id || 0)) + 1
							}

							this.showNotification(
								`Импортировано вопросов: ${importedCount} (заменены существующие)`,
								'success'
							)
							this.activeTab = 'questions'
							this.closeDocxImportModal()
						} else {
							this.showNotification(
								'Не найдено вопросов для импорта',
								'warning'
							)
						}
					} catch (error) {
						console.error('Import error:', error)
						this.showNotification(
							`Ошибка при импорте: ${error.message}`,
							'error'
						)
					}
				}

				reader.onerror = () => {
					this.showNotification('Ошибка при чтении файла', 'error')
				}

				reader.readAsArrayBuffer(file)
			}

			input.click()
		},

		extractDocxPlainText(documentXml) {
			const parser = new DOMParser()
			const xml = parser.parseFromString(documentXml, 'application/xml')

			// WordprocessingML:
			// - paragraph: w:p
			// - run: w:r
			// - text: w:t
			// - line break: w:br
			// - tab: w:tab
			const paragraphs = Array.from(xml.getElementsByTagName('w:p'))
			if (!paragraphs.length) return ''

			const lines = paragraphs.map(p => {
				let line = ''
				const children = p.getElementsByTagName('*')
				for (const node of children) {
					if (!node || !node.tagName) continue
					if (node.tagName === 'w:t') {
						line += node.textContent || ''
					} else if (node.tagName === 'w:tab') {
						line += '\t'
					} else if (node.tagName === 'w:br') {
						line += '\n'
					}
				}
				return line.trimEnd()
			})

			// Убираем хвостовые пустые строки, но сохраняем структуру документа.
			while (lines.length && !lines[lines.length - 1]) lines.pop()
			return lines.join('\n')
		},

		async parseDOCX(arrayBuffer) {
			const zip = await JSZip.loadAsync(arrayBuffer)
			const docFile = zip.file('word/document.xml')
			if (!docFile) {
				throw new Error('В файле .docx не найден word/document.xml')
			}
			const xmlText = await docFile.async('text')
			const plainText = this.extractDocxPlainText(xmlText)
			return this.parseDocxQuestionsText(plainText)
		},

		parseDocxQuestionsText(text) {
			const normalizeLine = s =>
				String(s || '')
					.replace(/\u00a0/g, ' ')
					.replace(/\r/g, '')
					.trimEnd()

			let lines = String(text || '')
				.split('\n')
				.map(normalizeLine)

			// Если есть явный маркер "Вопросы теста:", начинаем после него.
			const markerIdx = lines.findIndex(l =>
				/^вопросы\s+теста\s*:?\s*$/i.test(String(l || '').trim())
			)
			if (markerIdx >= 0) {
				lines = lines.slice(markerIdx + 1)
			}

			const parseOptionLine = l => {
				const m = String(l || '').match(/^\s*([A-ZА-Я])\)\s*(.*)$/i)
				if (!m) return null
				return {
					letter: String(m[1] || '').toUpperCase(),
					text: String(m[2] || '').trim(),
				}
			}

			const parseCorrectMarker = l => {
				const m = String(l || '')
					.trim()
					.match(/^правильн(ый|ые)\s+ответ(ы)?\s*:?\s*(.*)$/i)
				if (!m) return null
				return { rest: String(m[3] || '').trim() }
			}

			const parseExplanationMarker = l => {
				const m = String(l || '')
					.trim()
					.match(/^объяснени(е|я)\s*:?\s*(.*)$/i)
				if (!m) return null
				return { rest: String(m[2] || '').trim() }
			}

			const extractCorrectLettersFromText = s => {
				const str = String(s || '').trim()
				if (!str) return []
				const explicitMatches = Array.from(
					str.matchAll(/([\p{L}])\)\s*/giu)
				)
				if (explicitMatches.length) {
					return explicitMatches.map(m => String(m[1] || '').toUpperCase())
				}

				const normalized = str.replace(/[.;:]+$/g, '')
				const tokens = normalized
					.split(/[\s,;\/]+/)
					.map(token => token.replace(/[()\[\]{}]/g, '').trim())
					.filter(Boolean)

				return tokens
					.filter(token => /^[\p{L}]$/u.test(token))
					.map(token => token.toUpperCase())
			}

			const questions = []
			let current = null
			let inCorrectSection = false
			let inExplanationSection = false
			let explanationBuffer = []

			const finalizeCurrent = () => {
				if (!current) return
				if (!current.text) {
					current = null
					return
				}

				const hasAnswers =
					Array.isArray(current.answers) && current.answers.length > 0
				const correctLetters = Array.from(current.correctLetters || [])
				let type = hasAnswers ? 'single' : 'text'
				if (hasAnswers && correctLetters.length > 1) type = 'multiple'

				const out = {
					text: current.text,
					type,
					explanation: current.explanation || '',
					tags: '',
					customPoints: null,
				}

				if (hasAnswers) {
					out.answers = current.answers.map(a => ({
						text: a.text,
						isCorrect: correctLetters.includes(a.letter),
					}))
				} else if (current.correctText) {
					out.correctText = current.correctText
				}

				questions.push(out)
				current = null
			}

			for (const rawLine of lines) {
				const trimmed = String(rawLine || '').trim()

				// Пустая строка завершает многострочное объяснение.
				if (!trimmed) {
					if (inExplanationSection && current) {
						current.explanation = explanationBuffer.join('\n').trim()
						inExplanationSection = false
						explanationBuffer = []
					}
					continue
				}

				const correctMarker = parseCorrectMarker(trimmed)
				if (correctMarker) {
					if (!current) continue

					// "Правильный ответ: B) Хром" (в одной строке)
					if (correctMarker.rest) {
						const letters = extractCorrectLettersFromText(correctMarker.rest)
						if (letters.length) {
							letters.forEach(l => current.correctLetters.add(l))
						} else {
							// Для текстового вопроса (без вариантов) считаем остаток ответом.
							current.correctText = correctMarker.rest
						}
						inCorrectSection = false
					} else {
						inCorrectSection = true
					}
					continue
				}

				const explanationMarker = parseExplanationMarker(trimmed)
				if (explanationMarker) {
					if (!current) continue
					if (explanationMarker.rest) {
						current.explanation = explanationMarker.rest
						inExplanationSection = false
						explanationBuffer = []
					} else {
						inExplanationSection = true
						explanationBuffer = []
					}
					continue
				}

				if (inExplanationSection) {
					if (!current) {
						inExplanationSection = false
						explanationBuffer = []
						continue
					}
					explanationBuffer.push(trimmed)
					continue
				}

				if (inCorrectSection) {
					if (!current) {
						inCorrectSection = false
						continue
					}

					const opt = parseOptionLine(trimmed)
					if (opt) {
						current.correctLetters.add(opt.letter)
						continue
					}

					// Если у вопроса есть варианты и уже найден хотя бы один правильный,
					// то следующая обычная строка с высокой вероятностью является новым вопросом.
					if (
						Array.isArray(current.answers) &&
						current.answers.length > 0 &&
						current.correctLetters &&
						current.correctLetters.size > 0
					) {
						inCorrectSection = false
						finalizeCurrent()
						const cleaned = trimmed.replace(/^\s*\d+[\)\.\-:]\s*/, '')
						current = {
							text: cleaned,
							answers: [],
							correctLetters: new Set(),
							correctText: '',
							explanation: '',
						}
						continue
					}

					// Для текстового вопроса (без вариантов) считаем строку ответом.
					current.correctText = trimmed
					inCorrectSection = false
					continue
				}

				const opt = parseOptionLine(trimmed)
				if (opt) {
					if (current) current.answers.push(opt)
					continue
				}

				// Новая строка вопроса.
				finalizeCurrent()

				const cleaned = trimmed.replace(/^\s*\d+[\)\.\-:]\s*/, '')
				current = {
					text: cleaned,
					answers: [],
					correctLetters: new Set(),
					correctText: '',
					explanation: '',
				}
			}

			finalizeCurrent()
			return { questions }
		},

		createQuestionFromImport(importedQuestion) {
			if (!importedQuestion) return null

			const question = this.createQuestion(importedQuestion.type || 'single')

			question.text = importedQuestion.text || ''
			question.type = importedQuestion.type || 'single'
			question.correctText = importedQuestion.correctText || ''
			question.caseSensitive = importedQuestion.caseSensitive || false
			question.explanation = importedQuestion.explanation || ''
			question.tags = importedQuestion.tags || ''
			question.customPoints = importedQuestion.customPoints || null
			question.image = importedQuestion.image || null
			question.imageFile = null
			question.isExpanded = false
			question.isPersisted = false

			if (importedQuestion.answers && Array.isArray(importedQuestion.answers)) {
				question.answers = importedQuestion.answers.map(a => ({
					id: this.nextAnswerId++,
					text: a.text || '',
					isCorrect: a.isCorrect || false,
					image: a.image || null,
					imageFile: null,
					isPersisted: false,
				}))
			}

			return question
		},

		importTestSettings(testData) {
			if (!testData) return

			if (testData.title) this.testName = testData.title
			if (testData.description) this.testDescription = testData.description
			if (testData.module) this.selectedModule = testData.module
			if (testData.category) this.category = testData.category

			const s = testData.settings || testData
			if (s.duration !== undefined) this.duration = s.duration
			if (s.passingScore !== undefined) this.passingScore = s.passingScore
			if (s.attempts !== undefined) this.attempts = s.attempts
			if (s.pointsPerQuestion !== undefined)
				this.pointsPerQuestion = s.pointsPerQuestion
			if (s.randomOrder !== undefined) this.randomOrder = s.randomOrder
			if (s.showCorrectAnswers !== undefined)
				this.showCorrectAnswers = s.showCorrectAnswers
			if (s.allowReview !== undefined) this.allowReview = s.allowReview
			if (s.penalizeWrong !== undefined) this.penalizeWrong = s.penalizeWrong
			if (s.penaltyPercentage !== undefined)
				this.penaltyPercentage = s.penaltyPercentage
			if (s.disableCopying !== undefined) this.disableCopying = s.disableCopying
			if (s.limitTimePerQuestion !== undefined)
				this.limitTimePerQuestion = s.limitTimePerQuestion
			if (s.timePerQuestion !== undefined)
				this.timePerQuestion = s.timePerQuestion
			if (s.selectRandomQuestions !== undefined)
				this.selectRandomQuestions = s.selectRandomQuestions
			if (s.questionsToShow !== undefined)
				this.questionsToShow = s.questionsToShow
			if (s.questionDistributionMode !== undefined)
				this.questionDistributionMode = s.questionDistributionMode
			if (s.questionTypeDistribution !== undefined)
				this.questionTypeDistribution = s.questionTypeDistribution
		},

		async handleExport(format = 'json') {
			if (this.questions.length === 0) {
				this.showNotification('Нет вопросов для экспорта', 'warning')
				return
			}

			let blob, filename

			if (format === 'docx') {
				// Для .docx экспортируем только вопросы теста (без настроек).
				blob = await this.buildQuestionsDocxBlob(this.questions)
				filename = this.testName
					? `${this.sanitizeFilename(this.testName)}-${Date.now()}.docx`
					: `test-${Date.now()}.docx`
			} else {
				const data = {
					test: {
						title: this.testName,
						description: this.testDescription,
						module: this.selectedModule,
						category: this.category,
						settings: {
							duration: this.duration,
							passingScore: this.passingScore,
							attempts: this.attempts,
							pointsPerQuestion: this.pointsPerQuestion,
							randomOrder: this.randomOrder,
							showCorrectAnswers: this.showCorrectAnswers,
							allowReview: this.allowReview,
							penalizeWrong: this.penalizeWrong,
							penaltyPercentage: this.penaltyPercentage,
							disableCopying: this.disableCopying,
							limitTimePerQuestion: this.limitTimePerQuestion,
							timePerQuestion: this.timePerQuestion,
							selectRandomQuestions: this.selectRandomQuestions,
							questionsToShow: this.questionsToShow,
							questionDistributionMode: this.questionDistributionMode,
							questionTypeDistribution: { ...this.questionTypeDistribution },
						},
					},
					questions: this.questions.map((q, index) => ({
						id: index + 1,
						text: q.text,
						type: q.type,
						answers: (q.answers || []).map((a, aIndex) => ({
							id: aIndex + 1,
							text: a.text,
							isCorrect: a.isCorrect,
							image: a.image,
						})),
						correctText: q.correctText || '',
						caseSensitive: q.caseSensitive || false,
						explanation: q.explanation || '',
						tags: q.tags || '',
						customPoints: q.customPoints,
						image: q.image,
					})),
					exportedAt: new Date().toISOString(),
					version: '1.0',
				}

				const jsonString = JSON.stringify(data, null, 2)
				blob = new Blob([jsonString], { type: 'application/json' })
				filename = this.testName
					? `${this.sanitizeFilename(this.testName)}-${Date.now()}.json`
					: `test-${Date.now()}.json`
			}

			const url = URL.createObjectURL(blob)
			const a = document.createElement('a')
			a.href = url
			a.download = filename
			a.click()
			URL.revokeObjectURL(url)

			this.showNotification(`Тест экспортирован: ${filename}`, 'success')
		},

		escapeXml(value) {
			return String(value || '')
				.replace(/&/g, '&amp;')
				.replace(/</g, '&lt;')
				.replace(/>/g, '&gt;')
				.replace(/\"/g, '&quot;')
				.replace(/'/g, '&apos;')
		},

		async buildQuestionsDocxBlob(questions) {
			const safeQuestions = Array.isArray(questions) ? questions : []

			const lines = []
			lines.push('Вопросы теста:')
			lines.push('')

			const idxToLetter = idx =>
				String.fromCharCode('A'.charCodeAt(0) + Number(idx || 0))

			safeQuestions.forEach((q, qIndex) => {
				const qText = String(q?.text || '').trim()
				if (!qText) return

				lines.push(qText)

				const answers = Array.isArray(q?.answers) ? q.answers : []
				if (answers.length) {
					answers.forEach((a, aIndex) => {
						const letter = idxToLetter(aIndex)
						lines.push(`${letter}) ${String(a?.text || '').trim()}`)
					})
					lines.push('Правильный ответ:')
					const correctIdxs = answers
						.map((a, i) => (a?.isCorrect ? i : -1))
						.filter(i => i >= 0)
					if (correctIdxs.length) {
						for (const i of correctIdxs) {
							const letter = idxToLetter(i)
							lines.push(`${letter}) ${String(answers[i]?.text || '').trim()}`)
						}
					} else {
						lines.push('')
					}
				} else if (q?.type === 'text') {
					lines.push('Правильный ответ:')
					lines.push(String(q?.correctText || '').trim())
				}

				const explanation = String(q?.explanation || '').trim()
				if (explanation) {
					lines.push(`Объяснение: ${explanation}`)
				}

				if (qIndex !== safeQuestions.length - 1) lines.push('')
			})

			const paragraphsXml = lines
				.map(l => {
					const v = String(l || '')
					if (!v) return '<w:p/>'
					return `<w:p><w:r><w:t xml:space=\"preserve\">${this.escapeXml(
						v
					)}</w:t></w:r></w:p>`
				})
				.join('')

			const documentXml =
				'<?xml version=\"1.0\" encoding=\"UTF-8\" standalone=\"yes\"?>' +
				'<w:document xmlns:w=\"http://schemas.openxmlformats.org/wordprocessingml/2006/main\">' +
				'<w:body>' +
				paragraphsXml +
				'<w:sectPr/>' +
				'</w:body>' +
				'</w:document>'

			const relsXml =
				'<?xml version=\"1.0\" encoding=\"UTF-8\" standalone=\"yes\"?>' +
				'<Relationships xmlns=\"http://schemas.openxmlformats.org/package/2006/relationships\">' +
				'<Relationship Id=\"rId1\" Type=\"http://schemas.openxmlformats.org/officeDocument/2006/relationships/officeDocument\" Target=\"word/document.xml\"/>' +
				'</Relationships>'

			const contentTypesXml =
				'<?xml version=\"1.0\" encoding=\"UTF-8\" standalone=\"yes\"?>' +
				'<Types xmlns=\"http://schemas.openxmlformats.org/package/2006/content-types\">' +
				'<Default Extension=\"rels\" ContentType=\"application/vnd.openxmlformats-package.relationships+xml\"/>' +
				'<Default Extension=\"xml\" ContentType=\"application/xml\"/>' +
				'<Override PartName=\"/word/document.xml\" ContentType=\"application/vnd.openxmlformats-officedocument.wordprocessingml.document.main+xml\"/>' +
				'</Types>'

			const zip = new JSZip()
			zip.file('[Content_Types].xml', contentTypesXml)
			zip.folder('_rels').file('.rels', relsXml)
			zip.folder('word').file('document.xml', documentXml)

			return zip.generateAsync({
				type: 'blob',
				mimeType:
					'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
			})
		},

		sanitizeFilename(filename) {
			return filename
				.replace(/[^a-zA-Zа-яА-ЯёЁ0-9\s-]/g, '')
				.replace(/\s+/g, '-')
				.substring(0, 50)
				.toLowerCase()
		},

		// ============================================
		// TEST RUNNER
		// ============================================
		startTestPreview() {
			if (this.questions.length === 0) {
				this.showNotification('Нет вопросов для тестирования', 'warning')
				return
			}

			// Проверяем валидность вопросов перед запуском
			const invalidQuestions = this.questions.filter(q =>
				this.hasQuestionErrors(q)
			)

			if (invalidQuestions.length > 0) {
				const proceed = confirm(
					`${invalidQuestions.length} вопрос(ов) содержат ошибки. ` +
						'Всё равно запустить тест? Неполные вопросы могут работать некорректно.'
				)

				if (!proceed) return
			}

			this.showTestRunner = true
		},

		closeTestRunner() {
			this.showTestRunner = false
		},

		// ============================================
		// ЗАГРУЗКА И СБРОС ДАННЫХ
		// ============================================
		loadTestData(testData) {
			// 🔥 Блокируем watchers на время загрузки
			this.isLoadingData = true

			console.log('📥 Загрузка данных теста:', testData)
			console.log('📥 isScheduledPublish из testData:', testData.isScheduledPublish)
			console.log('📥 autoPublish из testData:', testData.autoPublish)
			console.log('📥 publishDate из testData:', testData.publishDate)
			console.log('📥 publishTime из testData:', testData.publishTime)

			this.editingTestId = testData.id || null
			this.testName = testData.title || ''
			this.testDescription = testData.description || ''
			this.selectedModule = testData.module || this.currentModuleId || ''
			this.category = testData.category || ''
			this.duration = testData.duration
			this.passingScore = testData.passingScore || 70
			this.attempts = testData.attempts || 1
			this.pointsPerQuestion = testData.pointsPerQuestion || 1

			// Дополнительные настройки
			this.randomOrder = testData.randomOrder ?? false
			this.showCorrectAnswers = testData.showCorrectAnswers ?? true
			this.allowReview = testData.allowReview ?? true
			this.penalizeWrong = testData.penalizeWrong ?? false
			this.penaltyPercentage = testData.penaltyPercentage ?? 50
			this.disableCopying = testData.disableCopying ?? false
			this.limitTimePerQuestion = testData.limitTimePerQuestion ?? false
			this.timePerQuestion = testData.timePerQuestion ?? 60
			this.selectRandomQuestions = testData.selectRandomQuestions ?? false
			this.questionsToShow = testData.questionsToShow ?? null
			this.questionDistributionMode =
				testData.questionDistributionMode || 'random'
			this.questionTypeDistribution = testData.questionTypeDistribution || {
				single: 0,
				multiple: 0,
				text: 0,
			}
			// Вычисляем isScheduledPublish из разных источников:
			// 1. Явное поле isScheduledPublish
			// 2. Наличие scheduled_publish_at
			// 3. autoPublish === false
			const hasScheduledPublishAt = !!(testData.scheduled_publish_at || testData.scheduledPublishAt)
			this.isScheduledPublish = testData.isScheduledPublish ?? hasScheduledPublishAt ?? (testData.autoPublish === false) ?? false
			
			// Парсим дату и время из scheduled_publish_at если есть
			if (hasScheduledPublishAt && !testData.publishDate) {
				const scheduledAt = testData.scheduled_publish_at || testData.scheduledPublishAt
				const [datePart, timePart] = scheduledAt.split(' ')
				this.publishDate = datePart || ''
				this.publishTime = timePart ? timePart.slice(0, 5) : ''
			} else {
				this.publishDate = testData.publishDate || ''
				this.publishTime = testData.publishTime || ''
			}

			console.log('📅 Автопубликация загружена:', {
				isScheduledPublish: this.isScheduledPublish,
				publishDate: this.publishDate,
				publishTime: this.publishTime,
				scheduled_publish_at: testData.scheduled_publish_at,
			})

			if (testData.questions && testData.questions.length > 0) {
				this.questions = testData.questions.map((q, index) => {
					const questionId =
						q.id ?? q.question_id ?? q.questionId ?? index + 1
					const rawType =
						q.type ?? q.question_type ?? q.questionType ?? 'text'
					const normalizedType =
						rawType === 'single_choice'
							? 'single'
							: rawType === 'multiple_choice'
							? 'multiple'
							: rawType
					const answers = Array.isArray(q.answers)
						? q.answers.map((a, answerIndex) => {
								if (typeof a === 'string') {
									return {
										id: answerIndex + 1,
										text: a,
										isCorrect: false,
										image: null,
										imageFile: null,
										isPersisted: false,
									}
								}
								return {
									...a,
									id:
										a.id ??
										a.answer_id ??
										a.answerId ??
										answerIndex + 1,
									text: a.text ?? a.answer_text ?? '',
									isCorrect: a.isCorrect ?? a.is_correct ?? false,
									image: a.image ?? a.image_url ?? a.imageUrl ?? null,
									imageFile: null,
									isPersisted: true,
								}
						  })
						: []

					const finalAnswers =
						normalizedType === 'text' ? [] : answers

					return {
						...q,
						id: questionId,
						text: q.text ?? q.question_text ?? q.title ?? '',
						type: normalizedType,
						answers: finalAnswers,
						correctText:
							q.correctText ??
							q.correct_text ??
							q.correct_answer ??
							'',
						caseSensitive:
							q.caseSensitive ?? q.case_sensitive ?? false,
						explanation: q.explanation ?? '',
						tags: q.tags ?? '',
						customPoints:
							q.customPoints ?? q.custom_points ?? q.points ?? null,
						image: q.image ?? q.image_url ?? q.imageUrl ?? null,
						imageFile: null,
						isExpanded: false,
						_previousType: normalizedType,
						isPersisted: true,
					}
				})

				this.nextQuestionId =
					Math.max(...this.questions.map(q => q.id || 0)) + 1

				const allAnswers = this.questions.flatMap(q => q.answers || [])
				if (allAnswers.length > 0) {
					this.nextAnswerId = Math.max(...allAnswers.map(a => a.id || 0)) + 1
				}
			}

			console.log('✅ Данные теста загружены:', {
				title: this.testName,
				questionsCount: this.questions.length,
			})

			// 🔥 Сохраняем снимок исходного состояния
			this.captureOriginalData()

			// 🔥 Разблокируем watchers
			this.isLoadingData = false
			this.isSaved = true // Данные загружены, изменений пока нет
		},

		resetForm() {
			this.editingTestId = null
			this.testName = ''
			this.testDescription = ''
			this.selectedModule = this.currentModuleId || ''
			this.category = ''
			this.duration = null
			this.passingScore = 70
			this.attempts = 1
			this.pointsPerQuestion = 1
			this.randomOrder = false
			this.showCorrectAnswers = true
			this.allowReview = true
			this.penalizeWrong = false
			this.penaltyPercentage = 50
			this.disableCopying = false
			this.limitTimePerQuestion = false
			this.timePerQuestion = 60
			this.selectRandomQuestions = false
			this.questionsToShow = null
			this.questionDistributionMode = 'random'
			this.questionTypeDistribution = { single: 0, multiple: 0, text: 0 }
			this.questions = []
			this.nextQuestionId = 1
			this.nextAnswerId = 1

			this.isScheduledPublish = false
			this.publishDate = ''
			this.publishTime = ''

			this.errors = {}
			this.showErrors = false
			this.activeTab = 'settings'

			this.isSaved = false
			this.originalData = null
		},

		// ============================================
		// ДЕЙСТВИЯ
		// ============================================
		async handleCreate() {
			this.showErrors = true

			// Валидация всех полей
			this.validateField('testName')
			this.validateField('selectedModule')

			if (!this.isFormValid) {
				this.showNotification('Заполните все обязательные поля', 'error')

				// Прокручиваем к первой ошибке
				if (!this.testName.trim() || !this.selectedModule) {
					this.activeTab = 'settings'
				} else {
					const firstErrorQuestion = this.questions.find(q =>
						this.hasQuestionErrors(q)
					)

					if (firstErrorQuestion) {
						this.activeTab = 'questions'
						this.$nextTick(() => {
							const questionIndex = this.questions.indexOf(firstErrorQuestion)
							const questionItems = document.querySelectorAll('.test-question')
							const errorItem = questionItems[questionIndex]

							if (errorItem) {
								errorItem.scrollIntoView({
									behavior: 'smooth',
									block: 'center',
								})
								firstErrorQuestion.isExpanded = true
							}
						})
					}
				}

				return
			}

			this.isSaving = true

			try {
				const testData = {
					...(this.editingTestId && { id: this.editingTestId }),
					title: this.testName.trim(),
					description: this.testDescription.trim(),
					module: this.selectedModule,
					category: this.category,
					duration: this.duration,
					passingScore: this.passingScore,
					attempts: this.attempts,
					pointsPerQuestion: this.pointsPerQuestion,
					randomOrder: this.randomOrder,
					showCorrectAnswers: this.showCorrectAnswers,
					allowReview: this.allowReview,
					penalizeWrong: this.penalizeWrong,
					penaltyPercentage: this.penaltyPercentage,
					disableCopying: this.disableCopying,
					limitTimePerQuestion: this.limitTimePerQuestion,
					timePerQuestion: this.timePerQuestion,
					selectRandomQuestions: this.selectRandomQuestions,
					questionsToShow: this.questionsToShow,
					questionDistributionMode: this.questionDistributionMode,
					questionTypeDistribution: { ...this.questionTypeDistribution },
					autoPublish: !this.isScheduledPublish,
					publishDate: this.publishDate,
					publishTime: this.publishTime,
					questions: this.questions.map(q => ({
						id: q.id,
						text: q.text?.trim(),
						type: q.type,
						answers: q.answers?.map(a => ({
							id: a.id,
							text: a.text?.trim(),
							isCorrect: a.isCorrect,
							image: a.image,
							imageFile: a.imageFile instanceof File ? a.imageFile : null,
						})),
						correctText: q.correctText?.trim(),
						caseSensitive: q.caseSensitive,
						explanation: q.explanation?.trim(),
						tags: q.tags?.trim(),
						customPoints: q.customPoints,
						image: q.image,
						imageFile: q.imageFile instanceof File ? q.imageFile : null,
					})),
					questionsCount: this.questions.length,
					totalPoints: this.totalPoints,
					passingPoints: this.passingPoints,
					selectRandomQuestions: this.selectRandomQuestions,
					questionsToShow: this.questionsToShow,
					questionDistributionMode: this.questionDistributionMode,
					questionTypeDistribution: { ...this.questionTypeDistribution },
				}

				this.isSaved = true
				if (typeof navigator !== 'undefined' && !navigator.onLine) {
					this.enqueueSubmit('save-test', testData)
					this.showNotification('Нет интернета. Сохранение теста поставлено в очередь.', 'warning')
					return
				}
				this.$emit('save-test', testData)
				this.clearLocalStorage()
				this.showNotification(
					this.isEditMode ? 'Тест обновлён' : 'Тест создан',
					'success'
				)
			} catch (error) {
				console.error('Error creating test:', error)
				this.showNotification('Ошибка при сохранении теста', 'error')
			} finally {
				this.isSaving = false
			}
		},

		handleSaveDraft() {
			if (this.isSaving) return
			this.isSaving = true
			const draftData = {
				...(this.editingTestId && { id: this.editingTestId }),
				title: this.testName || 'Черновик теста',
				description: this.testDescription,
				module: this.selectedModule,
				category: this.category,
				duration: this.duration,
				passingScore: this.passingScore,
				attempts: this.attempts,
				pointsPerQuestion: this.pointsPerQuestion,
				randomOrder: this.randomOrder,
				showCorrectAnswers: this.showCorrectAnswers,
				allowReview: this.allowReview,
				penalizeWrong: this.penalizeWrong,
				penaltyPercentage: this.penaltyPercentage,
				disableCopying: this.disableCopying,
				limitTimePerQuestion: this.limitTimePerQuestion,
				timePerQuestion: this.timePerQuestion,
				selectRandomQuestions: this.selectRandomQuestions,
				questionsToShow: this.questionsToShow,
				questionDistributionMode: this.questionDistributionMode,
				questionTypeDistribution: { ...this.questionTypeDistribution },
				questions: this.questions,
				questionsCount: this.questions.length,
				totalPoints: this.totalPoints,
				isDraft: true,
			}

			this.isSaved = true
			if (typeof navigator !== 'undefined' && !navigator.onLine) {
				this.enqueueSubmit('save-draft', draftData)
				this.showNotification('Нет интернета. Сохранение черновика поставлено в очередь.', 'warning')
				setTimeout(() => {
					this.isSaving = false
				}, 300)
				return
			}
			this.$emit('save-draft', draftData)
			this.clearLocalStorage()
			this.showNotification('Черновик сохранён', 'success')
			setTimeout(() => {
				this.isSaving = false
			}, 300)
		},
		openTestPreviewInNewTab() {
			if (!this.canOpenTestPreview) {
				alert('Сначала сохраните модуль и тест, затем откройте предпросмотр.')
				return
			}

			const moduleId = this.currentModuleId || this.selectedModule
			openInternalRouteInContext(this.$router, {
				name: 'TeacherScreen8Preview',
				query: {
					moduleId: String(moduleId),
					previewType: 'module',
					section: 'test',
					testId: String(this.editingTestId),
					testMode: '1',
				},
			}, {
				title: 'Предпросмотр теста',
				leftBoundary: 48,
			})
		},

    handleClose() {
      const hasRealChanges = this.hasUnsavedChanges

      if (hasRealChanges) {
        this.showConfirmDialog({
          title: 'Несохраненные изменения',
          message: 'У вас есть несохраненные изменения. Вы уверены, что хотите выйти?',
          confirmText: 'Выйти без сохранения',
          cancelText: 'Продолжить редактирование',
          onConfirm: () => {
            // 🔥 Сохраняем ID ДО очистки
            const scrollToId = this.editingTestId
            
            this.clearLocalStorage()
            
            // 🔥 Передаём ID для скролла
            this.$emit('cancel', { scrollToId, type: 'test' })
          },
        })
      } else {
        // 🔥 Сохраняем ID
        const scrollToId = this.editingTestId
        
        // 🔥 Передаём ID для скролла
        this.$emit('cancel', { scrollToId, type: 'test' })
      }
  },


		// ============================================
		// АВТОСОХРАНЕНИЕ
		// ============================================
		setupAutoSave() {
			this.autoSaveInterval = setInterval(() => {
				if (this.hasUnsavedChanges) {
					this.saveToLocalStorage()
				}
			}, 30000)
		},

		scheduleAutoSave() {
			if (this.autoSaveTimeout) {
				clearTimeout(this.autoSaveTimeout)
			}

			this.autoSaveTimeout = setTimeout(() => {
				this.saveToLocalStorage()
			}, 2000)
		},

		saveToLocalStorage() {
			if (this.editingTestId) return // Не сохраняем при редактировании

			const data = {
				testName: this.testName,
				testDescription: this.testDescription,
				selectedModule: this.selectedModule,
				category: this.category,
				duration: this.duration,
				passingScore: this.passingScore,
				attempts: this.attempts,
				pointsPerQuestion: this.pointsPerQuestion,
				randomOrder: this.randomOrder,
				showCorrectAnswers: this.showCorrectAnswers,
				allowReview: this.allowReview,
				penalizeWrong: this.penalizeWrong,
				penaltyPercentage: this.penaltyPercentage,
				disableCopying: this.disableCopying,
				limitTimePerQuestion: this.limitTimePerQuestion,
				timePerQuestion: this.timePerQuestion,
				selectRandomQuestions: this.selectRandomQuestions,
				questionsToShow: this.questionsToShow,
				questionDistributionMode: this.questionDistributionMode,
				questionTypeDistribution: { ...this.questionTypeDistribution },
				questions: this.questions.map(q => ({
					...q,
					// Не сохраняем base64 изображения в localStorage (слишком большие)
					image: q.image?.startsWith('data:') ? null : q.image,
					imageFile: null,
					answers: q.answers?.map(a => ({
						...a,
						image: a.image?.startsWith('data:') ? null : a.image,
						imageFile: null,
					})),
				})),
				isScheduledPublish: this.isScheduledPublish,
				publishDate: this.publishDate,
				publishTime: this.publishTime,
				timestamp: Date.now(),
			}

			try {
				localStorage.setItem(this.storageKey, JSON.stringify(data))
				this.lastSaved = new Date()
			} catch (error) {
				console.error('Error saving to localStorage:', error)
				// Если localStorage переполнен, очищаем и пробуем снова
				if (error.name === 'QuotaExceededError') {
					this.clearLocalStorage()
				}
			}
		},

		loadFromLocalStorage() {
			if (this.editingTestId) return

			try {
				const saved = localStorage.getItem(this.storageKey)
				if (!saved) return

				const data = JSON.parse(saved)

				// Проверяем срок годности (24 часа)
				const dayInMs = 24 * 60 * 60 * 1000
				if (data.timestamp && Date.now() - data.timestamp > dayInMs) {
					localStorage.removeItem(this.storageKey)
					return
				}

				// Восстанавливаем данные
				this.testName = data.testName || ''
				this.testDescription = data.testDescription || ''
				this.selectedModule = data.selectedModule || ''
				this.category = data.category || ''
				this.duration = data.duration
				this.passingScore = data.passingScore || 70
				this.attempts = data.attempts || 1
				this.pointsPerQuestion = data.pointsPerQuestion || 1
				this.randomOrder = data.randomOrder || false
				this.showCorrectAnswers = data.showCorrectAnswers !== false
				this.allowReview = data.allowReview !== false
				this.penalizeWrong = data.penalizeWrong || false
				this.penaltyPercentage = data.penaltyPercentage || 50
				this.disableCopying = data.disableCopying || false
				this.limitTimePerQuestion = data.limitTimePerQuestion || false
				this.timePerQuestion = data.timePerQuestion || 60
				this.selectRandomQuestions = data.selectRandomQuestions || false
				this.questionsToShow = data.questionsToShow || null
				this.questionDistributionMode =
					data.questionDistributionMode || 'random'
				this.questionTypeDistribution = data.questionTypeDistribution || {
					single: 0,
					multiple: 0,
					text: 0,
				}
				this.isScheduledPublish = data.isScheduledPublish || false
				this.publishDate = data.publishDate || ''
				this.publishTime = data.publishTime || ''

				if (data.questions && data.questions.length > 0) {
					this.questions = data.questions.map(q => ({
						...q,
						_previousType: q.type,
					}))

					this.nextQuestionId =
						Math.max(...data.questions.map(q => q.id || 0)) + 1

					const allAnswers = data.questions.flatMap(q => q.answers || [])
					if (allAnswers.length > 0) {
						this.nextAnswerId = Math.max(...allAnswers.map(a => a.id || 0)) + 1
					}
				}

				if (data.timestamp) {
					this.lastSaved = new Date(data.timestamp)
				}

				if (this.testName || this.questions.length > 0) {
					this.showNotification('Восстановлен черновик теста', 'info')
				}
			} catch (error) {
				console.error('Error loading from localStorage:', error)
				this.clearLocalStorage()
			}
		},

		clearLocalStorage() {
			try {
				localStorage.removeItem(this.storageKey)
				this.lastSaved = null
			} catch (error) {
				console.error('Error clearing localStorage:', error)
			}
		},

		handleBeforeUnload(e) {
			if (this.hasUnsavedChanges) {
				this.saveToLocalStorage()
				e.preventDefault()
				e.returnValue = ''
			}
		},

		// ============================================
		// CONFIRM DIALOG
		// ============================================
		showConfirmDialog(options) {
			this.confirmData = options
			this.showConfirm = true
		},

		confirmAction() {
			if (this.confirmData.onConfirm) {
				this.confirmData.onConfirm()
			}
			this.showConfirm = false
			this.confirmData = {}
		},

		cancelConfirm() {
			if (this.confirmData.onCancel) {
				this.confirmData.onCancel()
			}
			this.showConfirm = false
			this.confirmData = {}
		},

		// ============================================
		// УТИЛИТЫ
		// ============================================
		truncate(str, length) {
			if (!str) return ''
			return str.length > length ? str.substring(0, length) + '...' : str
		},

		getQuestionTypeLabel(type) {
			const typeObj = this.questionTypes.find(t => t.value === type)
			return typeObj?.label || type
		},

		getQuestionTypeIcon(type) {
			const typeObj = this.questionTypes.find(t => t.value === type)
			return typeObj?.icon || '?'
		},

		pluralize(number, one, two, five) {
			let n = Math.abs(number)
			n %= 100
			if (n >= 5 && n <= 20) return five
			n %= 10
			if (n === 1) return one
			if (n >= 2 && n <= 4) return two
			return five
		},

		formatDuration(minutes) {
			if (!minutes) return 'Без ограничения'
			if (minutes < 60) return `${minutes} мин`
			const hours = Math.floor(minutes / 60)
			const mins = minutes % 60
			return mins > 0 ? `${hours} ч ${mins} мин` : `${hours} ч`
		},

		// Копирование текста в буфер обмена
		async copyToClipboard(text) {
			try {
				await navigator.clipboard.writeText(text)
				this.showNotification('Скопировано в буфер обмена', 'success')
			} catch (error) {
				console.error('Failed to copy:', error)
				this.showNotification('Не удалось скопировать', 'error')
			}
		},
	},
}
</script>

<style scoped>
.form-offline-banner {
	margin-bottom: 12px;
	padding: 10px 12px;
	border: 1px solid #f59e0b;
	border-radius: 8px;
	background: #fffbeb;
	color: #92400e;
	font-size: 13px;
	font-weight: 500;
}

.test-actions__button--disabled {
	opacity: 0.5;
	cursor: not-allowed;
	background-color: #9ca3af !important;
	border: none !important;
	box-shadow: none !important;
}
.test-actions__button--primary:not(.test-actions__button--disabled):hover {
	opacity: 0.9;
	transform: translateY(-1px);
	transition: all 0.2s;
}

/* ============================================
   ВЫБОР СЛУЧАЙНЫХ ВОПРОСОВ
   ============================================ */

.test-random-selection {
	margin-top: 16px;
	padding-top: 16px;
	border-top: 1px solid #e0e0e0;
}

.test-question-pool-stats {
	display: flex;
	gap: 16px;
	padding: 12px 16px;
	background: #f5f7fa;
	border-radius: 8px;
	margin: 16px 0;
}

.pool-stat {
	display: flex;
	align-items: center;
	gap: 6px;
	font-size: 14px;
}

.pool-stat__icon {
	font-size: 16px;
	color: #667eea;
}

.pool-stat__label {
	color: #666;
}

.pool-stat__value {
	font-weight: 600;
	color: #333;
}

/* Режимы распределения */
.test-distribution-modes {
	display: grid;
	grid-template-columns: repeat(3, 1fr);
	gap: 12px;
	margin-top: 8px;
}

.test-distribution-mode {
	position: relative;
	cursor: pointer;
}

.test-distribution-mode__radio {
	position: absolute;
	opacity: 0;
}

.test-distribution-mode__content {
	display: flex;
	flex-direction: column;
	align-items: center;
	gap: 4px;
	padding: 16px 12px;
	background: #f9f9f9;
	border: 2px solid #e0e0e0;
	border-radius: 12px;
	transition: all 0.2s;
	text-align: center;
}

.test-distribution-mode--active .test-distribution-mode__content,
.test-distribution-mode__radio:checked + .test-distribution-mode__content {
	border-color: #667eea;
	background: rgba(102, 126, 234, 0.1);
}

.test-distribution-mode:hover .test-distribution-mode__content {
	border-color: #667eea;
}

.test-distribution-mode__icon {
	font-size: 24px;
}

.test-distribution-mode__title {
	font-weight: 600;
	font-size: 14px;
	color: #333;
}

.test-distribution-mode__desc {
	font-size: 12px;
	color: #666;
}

/* Настройки распределения */
.test-distribution-settings {
	margin-top: 16px;
	padding: 16px;
	background: #f9f9f9;
	border-radius: 12px;
}

.test-distribution-row {
	display: flex;
	flex-direction: column;
	gap: 12px;
}

.test-distribution-item {
	display: flex;
	align-items: center;
	justify-content: space-between;
	padding: 12px;
	background: #fff;
	border-radius: 8px;
}

.test-distribution-item__label {
	display: flex;
	align-items: center;
	gap: 8px;
	font-size: 14px;
	color: #333;
}

.test-distribution-item__icon {
	font-size: 16px;
	color: #667eea;
}

.test-distribution-item__available {
	font-size: 12px;
	color: #999;
	margin-left: 4px;
}

.test-distribution-item__input {
	display: flex;
	align-items: center;
	gap: 6px;
}

.test-distribution-item__input .test-field__input--small {
	width: 70px;
	text-align: center;
}

.test-distribution-item__suffix {
	font-size: 14px;
	color: #666;
	min-width: 24px;
}

/* Индикатор суммы */
.test-distribution-total {
	margin-top: 12px;
	padding: 10px 16px;
	border-radius: 8px;
	font-size: 14px;
	font-weight: 500;
	text-align: center;
	background: #f0f0f0;
	color: #666;
}

.test-distribution-total--valid {
	background: #e8f5e9;
	color: #2e7d32;
}

.test-distribution-total--warning {
	background: #fff3e0;
	color: #e65100;
}

.test-distribution-total--error {
	background: #ffebee;
	color: #c62828;
}

/* Сообщение валидации */
.test-distribution-message {
	margin-top: 12px;
	padding: 12px 16px;
	border-radius: 8px;
	font-size: 14px;
}

.test-distribution-message--success {
	background: #e8f5e9;
	color: #2e7d32;
}

.test-distribution-message--error {
	background: #ffebee;
	color: #c62828;
}

/* Поле с суффиксом */
.test-field__row {
	display: flex;
	align-items: center;
	gap: 8px;
}

.test-field__suffix {
	font-size: 14px;
	color: #666;
}

/* Подсказка в чекбоксе */
.test-checkbox__hint {
	font-size: 12px;
	color: #999;
	margin-left: 4px;
}

/* Адаптивность */
@media (max-width: 768px) {
	.test-distribution-modes {
		grid-template-columns: 1fr;
	}

	.test-question-pool-stats {
		flex-direction: column;
		gap: 8px;
	}

	.test-distribution-item {
		flex-direction: column;
		align-items: flex-start;
		gap: 8px;
	}
}

/* ============================================
   СЕКЦИЯ ИМПОРТ/ЭКСПОРТ
   ============================================ */
.test-import-export {
	display: flex;
	flex-direction: column;
	gap: 0;
}

.test-import-export__item {
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 16px;
	padding: 16px 0;
}

.test-import-export__item:first-child {
	padding-top: 0;
}

.test-import-export__item:last-child {
	padding-bottom: 0;
}

.test-import-export__info {
	display: flex;
	align-items: center;
	gap: 16px;
	flex: 1;
}

.test-import-export__icon {
	font-size: 32px;
	width: 48px;
	height: 48px;
	display: flex;
	align-items: center;
	justify-content: center;
	background: #f3f4f6;
	border-radius: 10px;
	flex-shrink: 0;
}

.test-import-export__text {
	flex: 1;
}

.test-import-export__title {
	font-size: 15px;
	font-weight: 600;
	color: #111827;
	margin-bottom: 4px;
}

.test-import-export__description {
	font-size: 13px;
	color: #6b7280;
}

.test-import-export__divider {
	height: 1px;
	background: #e5e7eb;
	margin: 0;
}

.test-import-export__hint {
	display: flex;
	align-items: center;
	gap: 8px;
	margin-top: 16px;
	padding: 12px 16px;
	background: #f0f9ff;
	border: 1px solid #bae6fd;
	border-radius: 8px;
}

.test-import-export__hint-icon {
	font-size: 16px;
	flex-shrink: 0;
}

.test-import-export__hint-text {
	font-size: 12px;
	color: #0369a1;
}

/* Адаптивность */
@media (max-width: 640px) {
	.test-import-export__item {
		flex-direction: column;
		align-items: flex-start;
		gap: 12px;
	}

	.test-import-export__item .test-btn {
		width: 100%;
		justify-content: center;
	}
}

/* ============================================
   CSS ПЕРЕМЕННЫЕ
   ============================================ */
:root {
	--primary-color: #0095ec;
	--primary-light: #e6f3ff;
	--primary-dark: #0080cb;
	--border-color: #d1d5db;
	--border-light: #e5e7eb;
	--text-primary: #111827;
	--text-secondary: #4b5563;
	--text-muted: #6b7280;
	--background-light: #f9fafb;
	--error-color: #e11d48;
	--success-color: #059669;
	--warning-color: #f59e0b;
	--border-radius: 6px;
}

/* ============================================
   ОСНОВНОЙ КОНТЕЙНЕР
   ============================================ */
.test-creation-form {
	font-family: system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI',
		sans-serif;
	min-height: 100vh;
	color: #1f2933;
	box-sizing: border-box;
}

.test-creation-form__container {
	position: relative;
	max-width: 1100px;
	margin: 0 auto;
	background: #ffffff;
	border-radius: 12px;
	border: 1px solid #e5e7eb;
	padding: 0 0 0;
	box-sizing: border-box;
}

/* ============================================
   ШАПКА
   ============================================ */
.test-settings {
	padding: 0;
}

.test-settings__header {
	display: flex;
	align-items: center;
	justify-content: space-between;
	background: #0095ec99;
	padding: 16px 24px;
	border-radius: 12px 12px 0 0;
}

.test-settings__header-title {
	font-size: 18px;
	font-weight: 600;
	color: #ffffff;
	margin: 0;
	display: flex;
	align-items: center;
	gap: 10px;
}

.test-settings__validation-badge {
	font-size: 16px;
	cursor: help;
}

.test-settings__validation-badge--error {
	animation: pulse 2s infinite;
}

@keyframes pulse {
	0%,
	100% {
		opacity: 1;
	}
	50% {
		opacity: 0.5;
	}
}

.test-settings__close-button {
	width: 28px;
	height: 28px;
	border: none;
	background: transparent;
	display: inline-flex;
	align-items: center;
	justify-content: center;
	cursor: pointer;
	transition: opacity 0.15s ease;
}

.test-settings__close-button:hover {
	opacity: 0.8;
}

.test-settings__close-icon {
	width: 18px;
	height: 18px;
	filter: brightness(0) invert(1);
}

/* ============================================
   ТАБЫ
   ============================================ */
.test-tabs {
	display: flex;
	border-bottom: 1px solid #e5e7eb;
	background: #f9fafb;
}

.test-tabs__item {
	flex: 1;
	padding: 14px 20px;
	background: transparent;
	border: none;
	border-bottom: 3px solid transparent;
	font-size: 14px;
	font-weight: 500;
	color: #6b7280;
	cursor: pointer;
	transition: all 0.2s ease;
	display: flex;
	align-items: center;
	justify-content: center;
	gap: 8px;
	position: relative;
}

.test-tabs__item:hover {
	background: rgba(0, 149, 236, 0.05);
	color: #0095ec;
}

.test-tabs__item--active {
	color: #0095ec;
	border-bottom-color: #0095ec;
	background: #fff;
}

.test-tabs__icon {
	font-size: 16px;
}

.test-tabs__badge {
	background: #0095ec;
	color: #fff;
	font-size: 11px;
	padding: 2px 6px;
	border-radius: 10px;
	font-weight: 600;
	min-width: 18px;
	text-align: center;
}

/* ============================================
   АВТОСОХРАНЕНИЕ
   ============================================ */
.test-settings__autosave {
	padding: 8px 24px;
	background: #f9fafb;
	border-bottom: 1px solid #e5e7eb;
}

.test-settings__autosave-text {
	font-size: 11px;
	color: #9ca3af;
}

/* ============================================
   КОНТЕНТ ТАБА
   ============================================ */
.test-tab-content {
	padding: 24px;
}

/* ============================================
   СЕКЦИИ
   ============================================ */
.test-section {
	margin-bottom: 24px;
}

.test-section__title {
	color: #4b5563;
	font-size: 15px;
	font-weight: 600;
	margin: 0 0 12px 0;
}

.test-section__card {
	border-radius: 8px;
	border: 1px solid #e5e7eb;
	background: #ffffff;
	padding: 20px;
}

/* ============================================
   ПОЛЯ ФОРМЫ
   ============================================ */
.test-field {
	margin-bottom: 20px;
}

.test-field:last-child {
	margin-bottom: 0;
}

.test-field-row {
	display: grid;
	grid-template-columns: 1fr 1fr;
	gap: 20px;
}

.test-field__label {
	display: block;
	color: #4b5563;
	font-size: 14px;
	font-weight: 500;
	margin-bottom: 8px;
}

.test-field__required {
	color: #e11d48;
}

.test-field__input,
.test-field__textarea,
.test-field__select {
	width: 100%;
	padding: 10px 14px;
	background: #fff;
	border: 1px solid #d1d5db;
	border-radius: 6px;
	font-size: 14px;
	color: #111827;
	outline: none;
	transition: border-color 0.15s ease, box-shadow 0.15s ease;
	box-sizing: border-box;
}

.test-field__input::placeholder,
.test-field__textarea::placeholder {
	color: #9ca3af;
}

.test-field__input:focus,
.test-field__textarea:focus,
.test-field__select:focus {
	border-color: #0095ec;
	box-shadow: 0 0 0 2px rgba(0, 149, 236, 0.15);
}

.test-field__input--error {
	border-color: #e11d48 !important;
}

.test-field__input--small {
	width: 100px;
}

.test-field__textarea {
	resize: vertical;
	min-height: 80px;
}

.test-field__footer {
	display: flex;
	justify-content: space-between;
	align-items: center;
	margin-top: 6px;
}

.test-field__hint {
	font-size: 12px;
	color: #9ca3af;
}

.test-field__error {
	font-size: 12px;
	color: #e11d48;
}

/* Слайдер */
.test-field__slider-wrapper {
	display: flex;
	gap: 12px;
	align-items: center;
}

.test-field__slider {
	flex: 1;
	height: 6px;
	border-radius: 3px;
	outline: none;
	-webkit-appearance: none; /* Для Safari/Chrome */
	-moz-appearance: none; /* Для Firefox */
	appearance: none; /* Стандартное свойство */
	background: #e5e7eb;
}

.test-field__slider::-webkit-slider-thumb {
	-webkit-appearance: none;
	width: 18px;
	height: 18px;
	border-radius: 50%;
	background: #0095ec;
	cursor: pointer;
}

/* ============================================
   ЧЕКБОКСЫ
   ============================================ */
.test-checkbox-group {
	display: flex;
	flex-direction: column;
	gap: 14px;
}

.test-checkbox {
	display: inline-flex;
	align-items: center;
	gap: 10px;
	cursor: pointer;
	user-select: none;
}

.test-checkbox--sm {
	margin-top: 8px;
}

.test-checkbox__input {
	position: absolute;
	opacity: 0;
	width: 0;
	height: 0;
}

.test-checkbox__custom {
	width: 20px;
	height: 20px;
	border: 2px solid #d1d5db;
	border-radius: 4px;
	display: inline-flex;
	align-items: center;
	justify-content: center;
	transition: all 0.15s ease;
	flex-shrink: 0;
}

.test-checkbox__input:checked + .test-checkbox__custom {
	background: #0095ec;
	border-color: #0095ec;
}

.test-checkbox__input:checked + .test-checkbox__custom::after {
	content: '✓';
	color: white;
	font-size: 12px;
	font-weight: 600;
}

.test-checkbox__text {
	font-size: 14px;
	color: #4b5563;
	display: flex;
	align-items: center;
	gap: 8px;
}

.test-checkbox__indent-field {
	margin-left: 30px;
	margin-top: 10px;
}

/* ============================================
   ТУЛБАР ВОПРОСОВ
   ============================================ */
.test-questions-toolbar {
	display: flex;
	justify-content: space-between;
	align-items: center;
	margin-bottom: 20px;
	gap: 16px;
	flex-wrap: wrap;
}

.test-questions-toolbar__left,
.test-questions-toolbar__right {
	display: flex;
	gap: 10px;
	align-items: center;
	flex-wrap: wrap;
}

/* ============================================
   КНОПКИ
   ============================================ */
.test-btn {
	display: inline-flex;
	align-items: center;
	gap: 6px;
	padding: 10px 16px;
	border: none;
	border-radius: 6px;
	font-size: 13px;
	font-weight: 500;
	cursor: pointer;
	transition: all 0.15s ease;
	white-space: nowrap;
}

.test-btn:disabled {
	opacity: 0.5;
	cursor: not-allowed;
}

.test-btn__icon {
	display: inline-flex;
	align-items: center;
	justify-content: center;
	font-size: 14px;
}

/* Для SVG иконок */
.test-btn__icon svg {
	width: 1em;
	height: 1em;
	vertical-align: middle;
}

.test-btn__icon--import {
	width: 16px;
	height: 16px;
	display: inline-block;
	background-color: currentColor;
	mask: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24'%3E%3Cpath d='M21,14a1,1,0,0,0-1,1v4a1,1,0,0,1-1,1H5a1,1,0,0,1-1-1V15a1,1,0,0,0-2,0v4a3,3,0,0,0,3,3H19a3,3,0,0,0,3-3V15A1,1,0,0,0,21,14Zm-9.71,1.71a1,1,0,0,0,.33.21.94.94,0,0,0,.76,0,1,1,0,0,0,.33-.21l4-4a1,1,0,0,0-1.42-1.42L13,12.59V3a1,1,0,0,0-2,0v9.59l-2.29-2.3a1,1,0,1,0-1.42,1.42Z'/%3E%3C/svg%3E")
		center/contain no-repeat;
	-webkit-mask: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24'%3E%3Cpath d='M21,14a1,1,0,0,0-1,1v4a1,1,0,0,1-1,1H5a1,1,0,0,1-1-1V15a1,1,0,0,0-2,0v4a3,3,0,0,0,3,3H19a3,3,0,0,0,3-3V15A1,1,0,0,0,21,14Zm-9.71,1.71a1,1,0,0,0,.33.21.94.94,0,0,0,.76,0,1,1,0,0,0,.33-.21l4-4a1,1,0,0,0-1.42-1.42L13,12.59V3a1,1,0,0,0-2,0v9.59l-2.29-2.3a1,1,0,1,0-1.42,1.42Z'/%3E%3C/svg%3E")
		center/contain no-repeat;
}

.test-btn--primary {
	background: #0095ec;
	color: #fff;
}

.test-btn--primary:hover:not(:disabled) {
	background: #0080cb;
	box-shadow: 0 4px 12px rgba(0, 149, 236, 0.35);
}

.test-btn--secondary {
	background: #f3f4f6;
	color: #4b5563;
}

.test-btn--secondary:hover:not(:disabled) {
	background: #e5e7eb;
}

.test-btn--ghost {
	background: transparent;
	color: #0095ec;
	border: 1px solid #0095ec;
}

.test-btn--ghost:hover {
	background: rgba(0, 149, 236, 0.05);
}

.test-btn--danger {
	background: #e11d48;
	color: #fff;
}

.test-btn--danger:hover {
	background: #be123c;
}

.test-btn--large {
	padding: 12px 24px;
	font-size: 14px;
}

.test-btn--sm {
	padding: 6px 12px;
	font-size: 12px;
}

/* ============================================
   ПОИСК
   ============================================ */
.test-search {
	position: relative;
	min-width: 220px;
}

.test-search__icon {
	position: absolute;
	left: 12px;
	top: 50%;
	transform: translateY(-50%);
	font-size: 14px;
	opacity: 0.5;
}

.test-search__input {
	width: 100%;
	padding: 10px 12px 10px 36px;
	border: 1px solid #d1d5db;
	border-radius: 6px;
	font-size: 13px;
	outline: none;
	transition: all 0.15s ease;
}

.test-search__input:focus {
	border-color: #0095ec;
	box-shadow: 0 0 0 2px rgba(0, 149, 236, 0.15);
}

/* ============================================
   ПУСТОЕ СОСТОЯНИЕ
   ============================================ */
.test-empty-state {
	text-align: center;
	padding: 60px 20px;
	background: #f9fafb;
	border-radius: 10px;
	border: 2px dashed #e5e7eb;
}

.test-empty-state--small {
	padding: 40px 20px;
}

.test-empty-state__icon {
	font-size: 60px;
	margin: 0 auto 16px;
	opacity: 0.4;
}

.test-empty-state__title {
	font-size: 18px;
	font-weight: 600;
	color: #111827;
	margin: 0 0 8px 0;
}

.test-empty-state__text {
	font-size: 14px;
	color: #6b7280;
	margin: 0 0 20px 0;
}

/* ============================================
   СПИСОК ВОПРОСОВ
   ============================================ */
.test-questions-list {
	display: flex;
	flex-direction: column;
	gap: 12px;
}

.test-question {
	background: #fff;
	border: 1px solid #e5e7eb;
	border-radius: 8px;
	overflow: hidden;
	transition: all 0.2s ease;
}

.test-question:hover {
	box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}

.test-question--expanded {
	border-color: #0095ec;
}

.test-question--error {
	border-color: #e11d48;
}

.test-question__header {
	display: flex;
	justify-content: space-between;
	align-items: center;
	padding: 14px 16px;
	background: #f9fafb;
	cursor: pointer;
	user-select: none;
}

.test-question__left,
.test-question__right {
	display: flex;
	align-items: center;
	gap: 10px;
}

.test-question__drag {
	width: 24px;
	height: 24px;
	display: flex;
	align-items: center;
	justify-content: center;
	background: transparent;
	border: none;
	cursor: grab;
	color: #9ca3af;
	font-size: 14px;
}

.test-question__drag:active {
	cursor: grabbing;
}

.test-question__number {
	font-weight: 600;
	color: #0095ec;
	font-size: 14px;
	display: flex;
	align-items: center;
	gap: 6px;
}

.test-question__error-badge {
	font-size: 12px;
	animation: pulse 2s infinite;
}

.test-question__preview {
	font-size: 13px;
	color: #6b7280;
	max-width: 300px;
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
}

.test-question__preview--empty {
	font-style: italic;
	opacity: 0.5;
}

.test-question__type-badge {
	background: #e6f3ff;
	color: #0095ec;
	padding: 4px 10px;
	border-radius: 10px;
	font-size: 11px;
	font-weight: 500;
}

.test-question__action {
	width: 28px;
	height: 28px;
	display: flex;
	align-items: center;
	justify-content: center;
	background: transparent;
	border: 1px solid #e5e7eb;
	border-radius: 4px;
	cursor: pointer;
	color: #6b7280;
	transition: all 0.15s ease;
	font-size: 14px;
}

.test-question__action:hover {
	background: #f3f4f6;
	color: #0095ec;
	border-color: #0095ec;
}

.test-question__action--danger:hover {
	background: #fee2e2;
	color: #e11d48;
	border-color: #e11d48;
}

.test-question__content {
	padding: 20px;
	border-top: 1px solid #e5e7eb;
}

/* ============================================
   ТИПЫ ВОПРОСОВ
   ============================================ */
.test-question-types {
	display: grid;
	grid-template-columns: repeat(3, 1fr);
	gap: 12px;
}

.test-question-type {
	position: relative;
	padding: 16px;
	border: 1px solid #e5e7eb;
	border-radius: 8px;
	cursor: pointer;
	transition: all 0.2s ease;
	text-align: center;
}

.test-question-type:hover {
	border-color: #0095ec;
	background: rgba(0, 149, 236, 0.02);
}

.test-question-type--active {
	border-color: #0095ec;
	background: #e6f3ff;
}

.test-question-type__radio {
	position: absolute;
	opacity: 0;
}

.test-question-type__icon {
	font-size: 28px;
	margin-bottom: 8px;
}

.test-question-type__label {
	font-weight: 600;
	color: #111827;
	font-size: 13px;
	margin-bottom: 4px;
}

.test-question-type__description {
	font-size: 11px;
	color: #9ca3af;
}

/* ============================================
   ЗАГРУЗКА ИЗОБРАЖЕНИЙ
   ============================================ */
.test-image-upload__preview {
	position: relative;
	display: inline-block;
	border-radius: 6px;
	overflow: hidden;
}

.test-image-upload__img {
	max-width: 250px;
	max-height: 150px;
	display: block;
	border-radius: 6px;
}

.test-image-upload__remove {
	position: absolute;
	top: 6px;
	right: 6px;
	width: 24px;
	height: 24px;
	background: rgba(225, 29, 72, 0.9);
	color: #fff;
	border: none;
	border-radius: 50%;
	cursor: pointer;
	display: flex;
	align-items: center;
	justify-content: center;
	font-size: 16px;
}

.test-image-upload__button {
	display: flex;
	align-items: center;
	gap: 8px;
	padding: 10px 16px;
	background: #f9fafb;
	border: 2px dashed #d1d5db;
	border-radius: 6px;
	color: #6b7280;
	cursor: pointer;
	transition: all 0.2s ease;
	font-size: 13px;
}

.test-image-upload__button:hover {
	background: #e6f3ff;
	border-color: #0095ec;
	color: #0095ec;
}

.test-image-upload__icon {
	font-size: 18px;
}

/* ============================================
   ОТВЕТЫ
   ============================================ */
.test-answers-header {
	display: flex;
	justify-content: space-between;
	align-items: center;
	margin-bottom: 12px;
}

.test-answers-list {
	display: flex;
	flex-direction: column;
	gap: 8px;
}

.test-answer-wrapper {
	display: flex;
	flex-direction: column;
	gap: 6px;
}

.test-answer {
	display: flex;
	align-items: center;
	gap: 10px;
	padding: 10px 12px;
	background: #f9fafb;
	border: 1px solid #e5e7eb;
	border-radius: 6px;
	transition: all 0.15s ease;
}

.test-answer:hover {
	background: #fff;
	border-color: #0095ec;
}

.test-answer--correct {
	background: #ecfdf5;
	border-color: #059669;
}

.test-answer__drag {
	width: 20px;
	height: 20px;
	display: flex;
	align-items: center;
	justify-content: center;
	background: transparent;
	border: none;
	cursor: grab;
	color: #9ca3af;
	font-size: 12px;
}

.test-answer__radio,
.test-answer__checkbox {
	width: 18px;
	height: 18px;
	cursor: pointer;
	accent-color: #0095ec;
	flex-shrink: 0;
}

.test-answer__input {
	flex: 1;
	padding: 8px 10px;
	background: #fff;
	border: 1px solid transparent;
	border-radius: 4px;
	font-size: 13px;
	outline: none;
	transition: all 0.15s ease;
}

.test-answer__input:focus {
	border-color: #0095ec;
	box-shadow: 0 0 0 2px rgba(0, 149, 236, 0.15);
}

.test-answer__image-btn {
	width: 28px;
	height: 28px;
	display: flex;
	align-items: center;
	justify-content: center;
	background: transparent;
	border: 1px solid #e5e7eb;
	border-radius: 4px;
	cursor: pointer;
	color: #6b7280;
	font-size: 12px;
	transition: all 0.15s ease;
}

.test-answer__image-btn:hover {
	background: #f3f4f6;
	border-color: #0095ec;
	color: #0095ec;
}

.test-answer__remove {
	width: 28px;
	height: 28px;
	display: flex;
	align-items: center;
	justify-content: center;
	background: transparent;
	border: 1px solid #e5e7eb;
	border-radius: 4px;
	cursor: pointer;
	color: #e11d48;
	font-size: 18px;
	transition: all 0.15s ease;
}

.test-answer__remove:hover:not(:disabled) {
	background: #fee2e2;
	border-color: #e11d48;
}

.test-answer__remove:disabled {
	opacity: 0.3;
	cursor: not-allowed;
}

.test-answer__image-preview {
	margin-left: 40px;
	position: relative;
	display: inline-block;
}

.test-answer__img {
	max-width: 150px;
	max-height: 100px;
	border-radius: 4px;
	display: block;
}

.test-answer__image-remove {
	position: absolute;
	top: 4px;
	right: 4px;
	width: 20px;
	height: 20px;
	background: rgba(225, 29, 72, 0.9);
	color: #fff;
	border: none;
	border-radius: 50%;
	cursor: pointer;
	display: flex;
	align-items: center;
	justify-content: center;
	font-size: 12px;
}

/* ============================================
   ДОПОЛНИТЕЛЬНЫЕ НАСТРОЙКИ ВОПРОСА
   ============================================ */
.test-question-advanced {
	margin-top: 16px;
	border: 1px solid #e5e7eb;
	border-radius: 6px;
	overflow: hidden;
}

.test-question-advanced__toggle {
	padding: 12px 14px;
	background: #f9fafb;
	cursor: pointer;
	font-weight: 500;
	color: #0095ec;
	font-size: 13px;
	user-select: none;
	list-style: none;
}

.test-question-advanced__toggle:hover {
	background: #f3f4f6;
}

.test-question-advanced__toggle::-webkit-details-marker {
	display: none;
}

.test-question-advanced__content {
	padding: 16px;
}

/* ============================================
   СТАТИСТИКА
   ============================================ */
.test-questions-stats {
	display: grid;
	grid-template-columns: repeat(3, 1fr);
	gap: 12px;
	margin-top: 20px;
	padding-top: 20px;
	border-top: 1px solid #e5e7eb;
}

.test-stat-card {
	text-align: center;
	padding: 16px;
	background: linear-gradient(135deg, #0095ec 0%, #0077c2 100%);
	color: #fff;
	border-radius: 8px;
}

.test-stat-card__icon {
	font-size: 24px;
	margin-bottom: 8px;
}

.test-stat-card__value {
	font-size: 24px;
	font-weight: 700;
	margin-bottom: 4px;
}

.test-stat-card__label {
	font-size: 12px;
	opacity: 0.9;
}

/* ============================================
   ПРЕДПРОСМОТР
   ============================================ */
.test-preview {
	max-width: 800px;
	margin: 0 auto;
}

.test-preview__header {
	text-align: center;
	margin-bottom: 24px;
	padding-bottom: 20px;
	border-bottom: 1px solid #e5e7eb;
}

.test-preview__title {
	font-size: 22px;
	font-weight: 700;
	color: #111827;
	margin: 0 0 8px 0;
}

.test-preview__description {
	font-size: 14px;
	color: #6b7280;
	margin: 0;
}

.test-preview__info-grid {
	display: grid;
	grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
	gap: 12px;
	margin-bottom: 24px;
	padding: 16px;
	background: #f9fafb;
	border-radius: 8px;
}

.test-preview__info-item {
	display: flex;
	align-items: center;
	gap: 8px;
	padding: 10px;
	background: #fff;
	border-radius: 6px;
}

.test-preview__info-icon {
	font-size: 16px;
}

.test-preview__info-label {
	font-size: 12px;
	color: #6b7280;
}

.test-preview__info-value {
	font-size: 14px;
	font-weight: 600;
	color: #111827;
	margin-left: auto;
}

.test-preview__questions {
	display: flex;
	flex-direction: column;
	gap: 20px;

	max-height: 600px;
	overflow-y: auto;
	padding-right: 8px;
}

/* Стили для скроллбара (опционально) */
.test-preview__questions::-webkit-scrollbar {
	width: 6px;
}

.test-preview__questions::-webkit-scrollbar-track {
	background: #f1f1f1;
	border-radius: 4px;
}

.test-preview__questions::-webkit-scrollbar-thumb {
	background: #c1c1c1;
	border-radius: 4px;
}

.test-preview__questions::-webkit-scrollbar-thumb:hover {
	background: #a8a8a8;
}

.test-preview-question {
	padding: 20px;
	background: #fff;
	border: 1px solid #e5e7eb;
	border-radius: 8px;
}

.test-preview-question__header {
	display: flex;
	justify-content: space-between;
	align-items: center;
	margin-bottom: 12px;
}

.test-preview-question__number {
	font-size: 13px;
	font-weight: 600;
	color: #0095ec;
}

.test-preview-question__points {
	font-size: 12px;
	color: #6b7280;
}

.test-preview-question__text {
	font-size: 16px;
	font-weight: 600;
	color: #111827;
	margin-bottom: 12px;
}

.test-preview-question__image {
	margin-bottom: 12px;
}

.test-preview-question__image img {
	max-width: 100%;
	border-radius: 6px;
}

.test-preview-question__type {
	display: inline-block;
	padding: 4px 10px;
	background: #e6f3ff;
	color: #0095ec;
	font-size: 11px;
	font-weight: 500;
	border-radius: 10px;
	margin-bottom: 12px;
}

.test-preview-answers {
	display: flex;
	flex-direction: column;
	gap: 8px;
}

.test-preview-answer {
	padding: 12px 14px;
	background: #f9fafb;
	border: 1px solid #e5e7eb;
	border-radius: 6px;
	display: flex;
	align-items: center;
	gap: 10px;
	flex-wrap: wrap;
}

.test-preview-answer--correct {
	background: #ecfdf5;
	border-color: #059669;
}

.test-preview-answer__marker {
	font-weight: 600;
	color: #0095ec;
	font-size: 13px;
}

.test-preview-answer__text {
	flex: 1;
	font-size: 14px;
	color: #4b5563;
}

.test-preview-answer__badge {
	background: #059669;
	color: #fff;
	font-size: 10px;
	padding: 3px 8px;
	border-radius: 10px;
	font-weight: 600;
}

.test-preview-answer__image {
	width: 100%;
	margin-top: 8px;
}

.test-preview-answer__image img {
	max-width: 150px;
	border-radius: 4px;
}

.test-preview-question__correct-text {
	padding: 12px 14px;
	background: #f9fafb;
	border: 1px solid #e5e7eb;
	border-radius: 6px;
	font-size: 14px;
	color: #4b5563;
}

.test-preview-question__badge {
	display: inline-block;
	margin-left: 8px;
	padding: 2px 8px;
	background: #f59e0b;
	color: #fff;
	font-size: 10px;
	border-radius: 10px;
	font-weight: 600;
}

.test-preview-question__explanation {
	margin-top: 12px;
	padding: 14px;
	background: #fffbeb;
	border-left: 3px solid #f59e0b;
	border-radius: 0 6px 6px 0;
	font-size: 13px;
	color: #6b7280;
}

.test-preview__actions {
	margin-top: 24px;
	padding-top: 20px;
	border-top: 1px solid #e5e7eb;
	text-align: center;
}

/* ============================================
   КНОПКИ ДЕЙСТВИЙ (ФУТЕР)
   ============================================ */
.test-actions {
	margin-top: 0;
	padding: 16px 24px;
	background: #f5f6f8;
	border-top: 1px solid #e5e7eb;
	display: flex;
	align-items: center;
	gap: 12px;
	border-radius: 0 0 12px 12px;
}

.test-actions__button {
	border-radius: 8px;
	font-size: 14px;
	font-weight: 500;
	padding: 10px 20px;
	border: 1px solid transparent;
	cursor: pointer;
	white-space: nowrap;
	transition: all 0.15s ease;
	display: inline-flex;
	align-items: center;
	gap: 8px;
}

.test-actions__button--ghost {
	background: transparent;
	color: #4b5563;
	border-color: transparent;
	margin-right: auto;
}

.test-actions__preview-tooltip {
	display: inline-flex;
	margin-right: auto;
}

.test-actions__button--ghost:hover:not(:disabled) {
	background: #e5e7eb;
}

.test-actions__button--ghost:disabled {
	opacity: 0.5;
	cursor: not-allowed;
}

.test-actions__button--secondary {
	background: #ffffff;
	color: #111827;
	border-color: #d1d5db;
}

.test-actions__button--secondary:hover:not(:disabled) {
	background: #f9fafb;
	box-shadow: 0 4px 10px rgba(148, 163, 184, 0.25);
}

.test-actions__button--secondary:disabled {
	opacity: 0.5;
	cursor: not-allowed;
}

.test-actions__button--primary {
	background: #0095ec;
	color: #ffffff;
	border-color: #0095ec;
	box-shadow: 0 4px 12px rgba(0, 149, 236, 0.35);
}

.test-actions__button--primary:hover:not(:disabled) {
	background: #0080cb;
}

.test-actions__button--primary:disabled {
	opacity: 0.6;
	cursor: not-allowed;
}

.test-actions__icon {
	font-size: 14px;
}

.test-actions__spinner {
	width: 16px;
	height: 16px;
	border: 2px solid rgba(255, 255, 255, 0.3);
	border-top-color: white;
	border-radius: 50%;
	animation: spin 0.8s linear infinite;
}

@keyframes spin {
	to {
		transform: rotate(360deg);
	}
}

/* ============================================
   МОДАЛЬНОЕ ОКНО
   ============================================ */
.test-modal-overlay {
	position: fixed;
	top: 0;
	left: 0;
	right: 0;
	bottom: 0;
	background: rgba(0, 0, 0, 0.5);
	display: flex;
	align-items: center;
	justify-content: center;
	z-index: 1000;
	padding: 20px;
	backdrop-filter: blur(2px);
}

.test-modal {
	background: #fff;
	border-radius: 12px;
	max-width: 480px;
	width: 100%;
	box-shadow: 0 20px 50px rgba(0, 0, 0, 0.25);
	overflow: hidden;
}

.test-modal__header {
	padding: 20px 24px 16px;
	border-bottom: 1px solid #e5e7eb;
}

.test-modal__title {
	margin: 0;
	font-size: 18px;
	font-weight: 600;
	color: #111827;
}

.test-modal__body {
	padding: 20px 24px;
}

.test-modal__body p {
	margin: 0;
	font-size: 14px;
	color: #6b7280;
	line-height: 1.6;
}

.docx-import__example {
	margin: 0;
	padding: 12px 14px;
	border: 1px solid #e5e7eb;
	border-radius: 12px;
	background: #ffffff;
	color: #111827;
	font-size: 13px;
	line-height: 1.55;
	white-space: pre-wrap;
}

.test-modal__footer {
	padding: 16px 24px;
	border-top: 1px solid #e5e7eb;
	display: flex;
	gap: 12px;
	justify-content: flex-end;
	background: #f9fafb;
}

/* ============================================
   АНИМАЦИИ
   ============================================ */
.test-expand-enter-active,
.test-expand-leave-active {
	transition: all 0.3s ease;
	overflow: hidden;
}

.test-expand-enter-from,
.test-expand-leave-to {
	opacity: 0;
	max-height: 0;
}

.test-expand-enter-to,
.test-expand-leave-from {
	opacity: 1;
	max-height: 2000px;
}

/* ============================================
   АДАПТИВНОСТЬ
   ============================================ */
@media (max-width: 900px) {
	.test-creation-form__container {
		margin: 0 12px;
	}

	.test-tab-content {
		padding: 16px;
	}

	.test-field-row {
		grid-template-columns: 1fr;
	}

	.test-question-types {
		grid-template-columns: 1fr;
	}

	.test-questions-stats {
		grid-template-columns: repeat(3, 1fr);
	}
}

@media (max-width: 768px) {
	.test-settings__header {
		padding: 14px 16px;
	}

	.test-settings__header-title {
		font-size: 16px;
	}

	.test-tabs__item {
		padding: 12px 10px;
		font-size: 12px;
	}

	.test-tabs__label {
		display: none;
	}

	.test-tabs__icon {
		font-size: 18px;
	}

	.test-tabs__badge {
		position: absolute;
		top: 4px;
		right: 4px;
		font-size: 10px;
		padding: 1px 5px;
	}

	.test-questions-toolbar {
		flex-direction: column;
		align-items: stretch;
	}

	.test-questions-toolbar__left,
	.test-questions-toolbar__right {
		width: 100%;
		flex-direction: column;
	}

	.test-search {
		min-width: 100%;
	}

	.test-question__preview {
		display: none;
	}

	.test-actions {
		padding: 12px 16px;
		flex-wrap: wrap;
	}

	.test-actions__button {
		font-size: 13px;
		padding: 8px 14px;
	}

	.test-actions__button--ghost {
		order: 3;
		width: 100%;
		margin-right: 0;
		margin-top: 8px;
		justify-content: center;
	}

	.test-actions__preview-tooltip {
		order: 3;
		width: 100%;
		margin-right: 0;
		margin-top: 8px;
	}

	.test-actions__preview-tooltip .test-actions__button--ghost {
		width: 100%;
		margin-right: 0;
		margin-top: 0;
		justify-content: center;
	}

	.test-preview__info-grid {
		grid-template-columns: 1fr 1fr;
	}

	.test-questions-stats {
		grid-template-columns: 1fr;
	}

	.test-answer {
		flex-wrap: wrap;
	}

	.test-answer__input {
		width: 100%;
		order: 3;
		margin-top: 8px;
	}
}

@media (max-width: 480px) {
	.test-tab-content {
		padding: 12px;
	}

	.test-section__card {
		padding: 14px;
	}

	.test-question__header {
		padding: 12px;
	}

	.test-question__content {
		padding: 14px;
	}

	.test-question__left {
		flex-wrap: wrap;
	}

	.test-question__number {
		width: 100%;
		margin-bottom: 6px;
	}

	.test-preview__info-grid {
		grid-template-columns: 1fr;
	}

	.test-empty-state {
		padding: 40px 16px;
	}

	.test-empty-state__icon {
		font-size: 48px;
	}

	.test-btn--large {
		width: 100%;
		justify-content: center;
	}

	.test-question-type {
		padding: 12px;
	}

	.test-question-type__icon {
		font-size: 22px;
	}

	.test-question-type__description {
		display: none;
	}
}

/* ============================================
   ПРОЧИЕ СТИЛИ
   ============================================ */

/* Состояние загрузки для изображений */
.test-image-loading {
	display: flex;
	align-items: center;
	justify-content: center;
	padding: 20px;
	background: #f9fafb;
	border: 2px dashed #e5e7eb;
	border-radius: 6px;
}

.test-image-loading__spinner {
	width: 24px;
	height: 24px;
	border: 2px solid #e5e7eb;
	border-top-color: #0095ec;
	border-radius: 50%;
	animation: spin 0.8s linear infinite;
}

/* Подсветка при drag & drop */
.test-question--dragging {
	opacity: 0.5;
	border-style: dashed;
}

.test-question--drag-over {
	border-color: #0095ec;
	background: rgba(0, 149, 236, 0.05);
}

.test-answer--dragging {
	opacity: 0.5;
}

.test-answer--drag-over {
	border-color: #0095ec;
}

/* Индикатор прогресса формы */
.test-progress {
	height: 4px;
	background: #e5e7eb;
	border-radius: 2px;
	overflow: hidden;
	margin: 0 24px 16px;
}

.test-progress__bar {
	height: 100%;
	background: linear-gradient(90deg, #0095ec 0%, #00c6ff 100%);
	border-radius: 2px;
	transition: width 0.3s ease;
}

/* Тултипы */
.test-tooltip {
	position: relative;
	display: inline-block;
}

.test-tooltip__content {
	position: absolute;
	bottom: 100%;
	left: 50%;
	transform: translateX(-50%);
	padding: 8px 12px;
	background: #1f2937;
	color: #fff;
	font-size: 12px;
	border-radius: 4px;
	white-space: nowrap;
	opacity: 0;
	visibility: hidden;
	transition: all 0.2s ease;
	z-index: 100;
	margin-bottom: 6px;
}

.test-tooltip__content::after {
	content: '';
	position: absolute;
	top: 100%;
	left: 50%;
	transform: translateX(-50%);
	border: 5px solid transparent;
	border-top-color: #1f2937;
}

.test-tooltip:hover .test-tooltip__content {
	opacity: 1;
	visibility: visible;
}

/* Состояние успеха/ошибки для полей */
.test-field__input--success {
	border-color: #059669;
}

.test-field__input--success:focus {
	box-shadow: 0 0 0 2px rgba(5, 150, 105, 0.15);
}

/* Бейдж статуса */
.test-status-badge {
	display: inline-flex;
	align-items: center;
	gap: 4px;
	padding: 4px 10px;
	border-radius: 12px;
	font-size: 11px;
	font-weight: 500;
}

.test-status-badge--draft {
	background: #fef3c7;
	color: #d97706;
}

.test-status-badge--published {
	background: #d1fae5;
	color: #059669;
}

.test-status-badge--scheduled {
	background: #e0e7ff;
	color: #4f46e5;
}

/* Счетчик символов */
.test-char-counter {
	font-size: 11px;
	color: #9ca3af;
	text-align: right;
	margin-top: 4px;
}

.test-char-counter--warning {
	color: #f59e0b;
}

.test-char-counter--error {
	color: #e11d48;
}

/* Разделитель секций */
.test-divider {
	height: 1px;
	background: #e5e7eb;
	margin: 24px 0;
}

.test-divider--dashed {
	background: transparent;
	border-top: 2px dashed #e5e7eb;
}

/* Карточка подсказки */
.test-hint-card {
	display: flex;
	gap: 12px;
	padding: 14px 16px;
	background: #f0f9ff;
	border: 1px solid #bae6fd;
	border-radius: 8px;
	margin-bottom: 16px;
}

.test-hint-card__icon {
	font-size: 20px;
	flex-shrink: 0;
}

.test-hint-card__content {
	flex: 1;
}

.test-hint-card__title {
	font-size: 13px;
	font-weight: 600;
	color: #0369a1;
	margin: 0 0 4px 0;
}

.test-hint-card__text {
	font-size: 12px;
	color: #0c4a6e;
	margin: 0;
	line-height: 1.5;
}

/* Лейбл с иконкой */
.test-label-with-icon {
	display: flex;
	align-items: center;
	gap: 6px;
}

.test-label-with-icon__icon {
	font-size: 14px;
}

/* Группа кнопок */
.test-button-group {
	display: flex;
	gap: 8px;
}

.test-button-group--vertical {
	flex-direction: column;
}

/* Скелетон загрузки */
.test-skeleton {
	background: linear-gradient(90deg, #f3f4f6 25%, #e5e7eb 50%, #f3f4f6 75%);
	background-size: 200% 100%;
	animation: skeleton-loading 1.5s infinite;
	border-radius: 4px;
}

.test-skeleton--text {
	height: 16px;
	width: 60%;
}

.test-skeleton--title {
	height: 24px;
	width: 40%;
}

.test-skeleton--button {
	height: 40px;
	width: 120px;
}

@keyframes skeleton-loading {
	0% {
		background-position: 200% 0;
	}
	100% {
		background-position: -200% 0;
	}
}

/* Оверлей загрузки */
.test-loading-overlay {
	position: absolute;
	top: 0;
	left: 0;
	right: 0;
	bottom: 0;
	background: rgba(255, 255, 255, 0.8);
	display: flex;
	align-items: center;
	justify-content: center;
	z-index: 10;
	border-radius: inherit;
}

.test-loading-overlay__spinner {
	width: 40px;
	height: 40px;
	border: 3px solid #e5e7eb;
	border-top-color: #0095ec;
	border-radius: 50%;
	animation: spin 0.8s linear infinite;
}

/* Уведомление */
.test-notification {
	position: fixed;
	bottom: 24px;
	right: 24px;
	padding: 14px 20px;
	background: #1f2937;
	color: #fff;
	border-radius: 8px;
	box-shadow: 0 10px 30px rgba(0, 0, 0, 0.2);
	display: flex;
	align-items: center;
	gap: 10px;
	z-index: 1100;
	animation: slide-up 0.3s ease;
}

.test-notification--success {
	background: #059669;
}

.test-notification--error {
	background: #e11d48;
}

.test-notification--warning {
	background: #d97706;
}

.test-notification__icon {
	font-size: 18px;
}

.test-notification__text {
	font-size: 14px;
}

.test-notification__close {
	margin-left: 10px;
	padding: 4px;
	background: transparent;
	border: none;
	color: rgba(255, 255, 255, 0.7);
	cursor: pointer;
	font-size: 18px;
	line-height: 1;
}

.test-notification__close:hover {
	color: #fff;
}

@keyframes slide-up {
	from {
		opacity: 0;
		transform: translateY(20px);
	}
	to {
		opacity: 1;
		transform: translateY(0);
	}
}

/* Аккордеон для мобильных */
.test-accordion {
	border: 1px solid #e5e7eb;
	border-radius: 8px;
	overflow: hidden;
}

.test-accordion__item {
	border-bottom: 1px solid #e5e7eb;
}

.test-accordion__item:last-child {
	border-bottom: none;
}

.test-accordion__header {
	padding: 14px 16px;
	background: #f9fafb;
	cursor: pointer;
	display: flex;
	justify-content: space-between;
	align-items: center;
	font-weight: 500;
	font-size: 14px;
	color: #111827;
	transition: background 0.15s ease;
}

.test-accordion__header:hover {
	background: #f3f4f6;
}

.test-accordion__icon {
	font-size: 12px;
	color: #6b7280;
	transition: transform 0.2s ease;
}

.test-accordion__item--open .test-accordion__icon {
	transform: rotate(180deg);
}

.test-accordion__content {
	padding: 16px;
}

/* Фокус для доступности */
.test-btn:focus-visible,
.test-field__input:focus-visible,
.test-field__textarea:focus-visible,
.test-field__select:focus-visible,
.test-tabs__item:focus-visible,
.test-question-type:focus-visible {
	outline: 2px solid #0095ec;
	outline-offset: 2px;
}

/* Скрытие для скринридеров */
.test-sr-only {
	position: absolute;
	width: 1px;
	height: 1px;
	padding: 0;
	margin: -1px;
	overflow: hidden;
	clip: rect(0, 0, 0, 0);
	white-space: nowrap;
	border: 0;
}

/* Печать */
@media print {
	.test-creation-form {
		background: white;
	}

	.test-settings__header,
	.test-tabs,
	.test-actions,
	.test-questions-toolbar,
	.test-question__action,
	.test-question__drag {
		display: none !important;
	}

	.test-question {
		break-inside: avoid;
		border: 1px solid #ccc;
		margin-bottom: 16px;
	}

	.test-question__content {
		display: block !important;
	}
}
</style>
